import { transport, BASE_PATH } from "@/src/config/apiConfig.js";
import { viewsTransport } from "@/src/config/viewsTransport.js";
import { marketingTransport } from "@/src/config/marketingTransport.js";
import WorkspaceService from "@/src/api/WorkspaceService.js";

const FEATURES_CACHE_KEY = "enabled_features_cache";
const FEATURES_TIMESTAMP_KEY = "enabled_features_timestamp";
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes in milliseconds

export default {
  getBasePath() {
    return BASE_PATH;
  },

  isDefined(val) {
    return typeof val !== "undefined" && val !== null;
  },

  async getAllDomains() {
    try {
      const res = await transport.get(`${BASE_PATH}/api/infraforge/domains`);
      return res.data;
    } catch (error) {
      console.error("An error occurred while executing getAllDomains: ", error);
      throw error;
    }
  },

  async getDomainDns(domainId) {
    try {
      const res = await transport.get(
        `${BASE_PATH}/api/infraforge/domain/${domainId}`,
      );
      return res.data.data || res.data;
    } catch (error) {
      console.error("An error occurred while executing getDomainDns: ", error);
      throw error;
    }
  },

  async updateDomainDns(domainId, payload) {
    try {
      const res = await transport.put(
        `${BASE_PATH}/api/infraforge/domain/${domainId}`,
        payload,
      );
      return true;
    } catch (error) {
      console.error(
        "An error occurred while executing updateDomainDns: ",
        error,
      );
      throw error;
    }
  },

  async generateMailboxes(payload) {
    try {
      let res = await transport.post(
        BASE_PATH + `/api/infraforge/generate/mailboxes`,
        payload,
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing generateMailboxes: ",
        error,
      );
      throw error;
    }
  },

  async purchaseMailboxes(payload) {
    try {
      let res = await transport.post(
        BASE_PATH + `/api/infraforge/purchase-mailbox`,
        payload,
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing purchaseMailboxes: ",
        error,
      );
      throw error;
    }
  },

  async generateDomains(primaryDomain, count) {
    try {
      // Split based on the first dot:
      // - "abc.com"    -> inputSld: "abc", outputTld: "com"
      // - "abc.co.in"  -> inputSld: "abc", outputTld: "co.in"
      const firstDotIndex = primaryDomain.indexOf(".");
      let inputSld = primaryDomain;
      let outputTld = "com";

      if (firstDotIndex !== -1) {
        inputSld = primaryDomain.slice(0, firstDotIndex);
        outputTld = primaryDomain.slice(firstDotIndex + 1);
      }

      const requestBody = {
        count: count || 5,
        inputSld,
        outputTld,
      };

      let res = await transport.post(
        `${BASE_PATH}/api/infraforge/domains`,
        requestBody,
      );

      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing generateDomains: ",
        error,
      );
      throw error;
    }
  },

  async checkDomainAvailability(domain) {
    try {
      let res = await transport.get(
        `${BASE_PATH}/api/infraforge/check-domain-availability`,
        { params: { domain } },
      );

      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing checkDomainAvailability: ",
        error,
      );
      throw error;
    }
  },

  async purchaseDomains(payload) {
    try {
      let res = await transport.post(
        `${BASE_PATH}/api/infraforge/purchase-domains`,
        payload,
      );

      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing purchaseDomains: ",
        error,
      );
      throw error;
    }
  },

  async createInfraforgeWorkspace() {
    try {
      let res = await transport.post(
        `${BASE_PATH}/api/infraforge/workspace`,
        {},
      );

      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing createInfraforgeWorkspace: ",
        error,
      );
      throw error;
    }
  },

  async saveContactDetails(contactData) {
    try {
      let res = await transport.post(
        `${BASE_PATH}/api/infraforge/save/contact-details`,
        contactData,
      );

      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing saveContactDetails: ",
        error,
      );
      throw error;
    }
  },

  async getContactDetails() {
    try {
      let res = await transport.get(
        `${BASE_PATH}/api/infraforge/contact-details`,
      );

      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing getContactDetails: ",
        error,
      );
      throw error;
    }
  },

  async getCreditRates() {
    try {
      let res = await transport.get(`${BASE_PATH}/api/infraforge/credit-rates`);

      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing getCreditRates: ",
        error,
      );
      throw error;
    }
  },

  setCurrentWorkspaceId(workspaceId) {
    localStorage.setItem("currentWorkspaceId", workspaceId);
    document.cookie = `workspace_id=${workspaceId}; path=/; max-age=31536000`;
    console.log("[DashboardService] Set workspace cookie:", workspaceId);
  },
  getCurrentWorkspaceId() {
    return localStorage.getItem("currentWorkspaceId");
  },

  async fetchWorkspaces() {
    try {
      let res = await transport.get(BASE_PATH + `/api/workspaces`, {
        params: {},
      });
      return res.data;
    } catch (error) {
      console.error("An error occurred while fetching workspaces: ", error);
      throw error;
    }
  },

  async fetchCurrentWorkspace() {
    try {
      let res = await transport.get(BASE_PATH + `/api/current_workspace`, {
        params: {},
      });
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while fetching current workspace: ",
        error,
      );
      throw error;
    }
  },

  async fetchCurrentWorkspaceBillingVersion() {
    try {
      let res = await transport.get(
        BASE_PATH + `/api/workspace_billing_version`,
        {
          params: {},
        },
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while fetching workspace billing version: ",
        error,
      );
      throw error;
    }
  },

  async fetchCurrentWorkspaceAppStoreVersion() {
    try {
      let res = await transport.get(
        BASE_PATH + `/api/workspace_app_store_version`,
        {
          params: {},
        },
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while fetching workspace app store version: ",
        error,
      );
      throw error;
    }
  },

  getCookie(name) {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop().split(";").shift();
    return null;
  },

  async createWorkspace(workspaceData) {
    try {
      const headers = {};
      const fbp = this.getCookie("_fbp");
      const fbc = this.getCookie("_fbc");

      if (fbp) {
        headers["X-FBP"] = fbp;
      }
      if (fbc) {
        headers["X-FBC"] = fbc;
      }

      let res = await transport.post(
        BASE_PATH + "/api/workspaces",
        workspaceData,
        { headers },
      );
      return res.data;
    } catch (error) {
      console.error("An error occurred while creating workspace: ", error);
      throw error;
    }
  },

  async updateWorkspace(workspaceData) {
    try {
      let res = await transport.put(
        BASE_PATH + `/api/workspaces`,
        workspaceData,
      );
      return res.data;
    } catch (error) {
      console.error("An error occurred while updating workspace: ", error);
      throw error;
    }
  },

  async getWorkspacesForDomain() {
    try {
      let res = await transport.get(
        `${BASE_PATH}/api/workspace_domains/workspaces`,
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while getting workspaces for domain: ",
        error,
      );
      throw error;
    }
  },

  async updateDomainsForWorkspace(payload) {
    try {
      let res = await transport.put(
        `${BASE_PATH}/api/workspace_domains`,
        payload,
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while updating domains for workspace: ",
        error,
      );
      throw error;
    }
  },

  async getDomainsForWorkspace() {
    try {
      let res = await transport.get(`${BASE_PATH}/api/workspace_domains`);
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while getting domains for workspace: ",
        error,
      );
      throw error;
    }
  },

  async joinWorkspace(workspaceId) {
    try {
      let res = await transport.post(`${BASE_PATH}/api/workspaces/join`, {
        workspace_id: workspaceId,
      });
      return res.data;
    } catch (error) {
      console.error("An error occurred while joining workspace: ", error);
      throw error;
    }
  },

  async getProfile() {
    try {
      let res = await transport.get(BASE_PATH + `/api/profile`, {
        params: {},
      });
      return res.data;
    } catch (error) {
      console.error("An error occurred while getting profile: ", error);
      throw error;
    }
  },

  async updateProfile(profileData) {
    try {
      let res = await transport.put(BASE_PATH + `/api/profile`, profileData);
      return res.data;
    } catch (error) {
      console.error("An error occurred while updating profile: ", error);
      throw error;
    }
  },

  async fetchCustomers(
    page = 0,
    pageSize = 20,
    searchQuery = "",
    filters = {},
  ) {
    try {
      const params = {
        page,
        page_size: pageSize,
        ...filters,
      };

      // Get customer lifecycle stages from settings
      try {
        const customerSettingsResponse = await this.fetchCustomerSettings();
        if (
          customerSettingsResponse.success &&
          customerSettingsResponse.customer_settings?.customer_lifecycle_stages
        ) {
          // Use the first customer lifecycle stage as the default filter
          params.lifecycle_stage =
            customerSettingsResponse.customer_settings.customer_lifecycle_stages[0];
        } else {
          // Fallback to 'Customer' if no settings exist
          params.lifecycle_stage = "Customer";
        }
      } catch (error) {
        console.error("Error fetching customer settings:", error);
        // Fallback to 'Customer' if there's an error
        params.lifecycle_stage = "Customer";
      }

      if (searchQuery) {
        params.search_query = searchQuery;
      }

      let res = await transport.get(BASE_PATH + "/api/companies/search", {
        params,
      });
      return res.data;
    } catch (error) {
      console.error("An error occurred while fetching customers: ", error);
      throw error;
    }
  },

  async searchExistingCompanies(params, config = {}) {
    try {
      let res = await transport.get(
        BASE_PATH + "/api/search_existing_companies",
        {
          params: params,
          ...config,
        },
      );
      return res.data;
    } catch (error) {
      if (error.code !== "ERR_CANCELED") {
        console.error(
          "An error occurred while searching existing companies: ",
          error,
        );
        throw error;
      }
    }
  },

  async searchExistingDeals(params, config = {}) {
    try {
      let res = await transport.get(`${BASE_PATH}/api/search_existing_deals`, {
        params: params,
        ...config,
      });
      return res.data;
    } catch (error) {
      if (error.code !== "ERR_CANCELED") {
        console.error(
          "An error occurred while searching existing deals: ",
          error,
        );
        throw error;
      }
    }
  },

  async fetchUsers(filter = "all") {
    try {
      let res = await transport.get(`${BASE_PATH}/api/get_users`, {
        params: {
          filter: filter,
        },
      });
      return res.data;
    } catch (error) {
      console.error("An error occurred while executing fetchUsers: ", error);
      throw error;
    }
  },

  async fetchEntityOwners(entityType) {
    try {
      let res = await transport.get(`${BASE_PATH}/api/entity_owners`, {
        params: {
          entity_type: entityType,
        },
      });
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing fetchEntityOwners: ",
        error,
      );
      throw error;
    }
  },

  async fetchPendingUsers() {
    try {
      let res = await transport.get(`${BASE_PATH}/api/pending_invites`);
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing fetchPendingUsers: ",
        error,
      );
      throw error;
    }
  },

  async fetchExpiredUserInvites() {
    try {
      let res = await transport.get(`${BASE_PATH}/api/expired_user_invites`);
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing fetchExpiredUserInvites: ",
        error,
      );
      throw error;
    }
  },

  async fetchTeams() {
    try {
      let res = await transport.get(`${BASE_PATH}/api/teams`);
      return res.data;
    } catch (error) {
      console.error("An error occurred while executing fetchTeams: ", error);
      throw error;
    }
  },

  async fetchTeamsForTransferCalls() {
    try {
      let res = await transport.get(`${BASE_PATH}/api/teams/transfer`);
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing fetchTeamsForTransferCalls: ",
        error,
      );
      throw error;
    }
  },

  async fetchTeamMembers(teamIds) {
    try {
      const response = await transport.get(`${BASE_PATH}/api/teams/members`, {
        params: { team_ids: teamIds },
      });
      return response.data;
    } catch (error) {
      console.error("Error fetching team members:", error);
      throw error;
    }
  },

  async verifyTeam(payload) {
    try {
      const res = await transport.post(
        `${BASE_PATH}/api/meeting_agent/verify_team`,
        payload,
      );
      return res.data;
    } catch (error) {
      console.error("An error occurred while executing verifyTeam: ", error);
      throw error;
    }
  },

  async fetchScoringEntities() {
    try {
      const res = await transport.get(`${BASE_PATH}/api/status/entities`);
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing fetchScoringEntities: ",
        error,
      );
      throw error;
    }
  },

  async fetchEntityScoringStatus(id) {
    try {
      const res = await transport.get(`${BASE_PATH}/api/status/${id}`);
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing fetchEntityScoringStatus: ",
        error,
      );
      throw error;
    }
  },

  async fetchEntityScoringCriteria(id) {
    try {
      const res = await transport.get(
        `${BASE_PATH}/api/v1/scoring-criteria/${id}`,
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing fetchEntityScoringCriteria: ",
        error,
      );
      throw error;
    }
  },

  async updateEntityScoringCriteria(id, payload) {
    try {
      const res = await transport.post(
        `${BASE_PATH}/api/v1/scoring-criteria/${id}`,
        payload,
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing updateEntityScoringCriteria: ",
        error,
      );
      throw error;
    }
  },

  async bulkProcessEntityScoring(payload) {
    try {
      const res = await transport.post(
        `${BASE_PATH}/api/v1/entity/bulk-process`,
        payload,
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing bulkProcessEntityScoring: ",
        error,
      );
      throw error;
    }
  },

  async updateEntityScoringStatus(id, payload) {
    try {
      const res = await transport.put(`${BASE_PATH}/api/status/${id}`, payload);
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing updateEntityScoringStatus: ",
        error,
      );
      throw error;
    }
  },

  async fetchUserPermissions() {
    try {
      let res = await transport.get(`${BASE_PATH}/api/user_permissions`);
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing fetchUserPermissions: ",
        error,
      );
      throw error;
    }
  },

  async updateRolePermissions(role_id, permissions) {
    try {
      let res = await transport.put(
        `${BASE_PATH}/api/roles/${role_id}/permissions`,
        permissions,
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing updateRolePermissions: ",
        error,
      );
      throw error;
    }
  },

  async fetchRolePermissions(role_id) {
    try {
      let res = await transport.get(
        `${BASE_PATH}/api/roles/${role_id}/permissions`,
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing fetchRolePermissions: ",
        error,
      );
      throw error;
    }
  },

  async createRole(request) {
    try {
      let res = await transport.post(`${BASE_PATH}/api/roles`, request);
      return res.data;
    } catch (error) {
      console.error("An error occurred while executing createRole: ", error);
      throw error;
    }
  },

  async updateRole(role_id, request) {
    try {
      let res = await transport.put(
        `${BASE_PATH}/api/roles/${role_id}`,
        request,
      );
      return res.data;
    } catch (error) {
      console.error("An error occurred while executing updateRole: ", error);
      throw error;
    }
  },

  async updateUserRole(user_id, params) {
    try {
      let res = await transport.put(
        `${BASE_PATH}/api/users/${user_id}/roles`,
        params,
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing updateUserRole: ",
        error,
      );
      throw error;
    }
  },

  async fetchRoles() {
    try {
      let res = await transport.get(`${BASE_PATH}/api/roles`);
      return res.data;
    } catch (error) {
      console.error("An error occurred while executing fetchRoles: ", error);
      throw error;
    }
  },

  async fetchRole(role_id) {
    try {
      let res = await transport.get(`${BASE_PATH}/api/roles/${role_id}`);
      return res.data;
    } catch (error) {
      console.error("An error occurred while executing fetchRole: ", error);
      throw error;
    }
  },

  async createTeam(request) {
    try {
      let res = await transport.post(`${BASE_PATH}/api/teams`, request);
      return res.data;
    } catch (error) {
      console.error("An error occurred while executing createTeam: ", error);
      throw error;
    }
  },

  async updateTeam(teamId, request) {
    try {
      let res = await transport.put(
        `${BASE_PATH}/api/teams/${teamId}`,
        request,
      );
      return res.data;
    } catch (error) {
      console.error("An error occurred while executing updateTeam: ", error);
      throw error;
    }
  },

  async deleteTeam(teamId) {
    try {
      let res = await transport.delete(`${BASE_PATH}/api/teams/${teamId}`);
      return res.data;
    } catch (error) {
      console.error("An error occurred while executing deleteTeam: ", error);
      throw error;
    }
  },
  async updateTimezone(timezone) {
    try {
      let res = await transport.post(
        BASE_PATH + "/api/update_profile_timezone",
        {
          timezone: timezone,
        },
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing updateTimezone: ",
        error,
      );
      throw error;
    }
  },

  async searchLeadsContacts(params) {
    try {
      let res = await transport.get(`${BASE_PATH}/api/search_leads_contacts`, {
        params,
      });
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing searchLeadsContacts: ",
        error,
      );
      throw error;
    }
  },

  async replaceEmailPlaceholderEntity(
    entityId,
    entityType,
    subject,
    body,
    step_type,
    assigned_to_id,
    ai_variables,
    sequenceId,
  ) {
    try {
      let res = await transport.post(
        BASE_PATH + "/api/outreach/replace_email_placeholders",
        {
          entity_id: entityId,
          entity_type: entityType,
          subject: subject,
          body: body,
          step_type: step_type,
          assigned_to_id: assigned_to_id,
          ai_variables: ai_variables,
          sequence_id: sequenceId,
        },
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing replaceEmailPlaceholderEntity: ",
        error,
      );
      throw error;
    }
  },

  async searchExistingContacts(params, config = {}) {
    try {
      let res = await transport.get(
        BASE_PATH + "/api/search_existing_contacts",
        {
          params: params,
          ...config,
        },
      );
      return res.data;
    } catch (error) {
      if (error.code !== "ERR_CANCELED") {
        console.error(
          "An error occurred while executing searchExistingContacts: ",
          error,
        );
        throw error;
      }
    }
  },

  async searchExistingLeads(params, config = {}) {
    try {
      let res = await transport.get(`${BASE_PATH}/api/search_existing_leads`, {
        params: params,
        ...config,
      });
      return res.data;
    } catch (error) {
      if (error.code !== "ERR_CANCELED") {
        console.error(
          "An error occurred while executing searchExistingLeads: ",
          error,
        );
        throw error;
      }
    }
  },

  async fetchCompanyContacts(company_id) {
    try {
      let res = await transport.get(BASE_PATH + "/api/company_contacts", {
        params: {
          company_id: company_id,
        },
      });
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing fetchCompanyContacts: ",
        error,
      );
      throw error;
    }
  },

  async fetchCompanyLeads(company_id) {
    try {
      let res = await transport.get(`${BASE_PATH}/api/company_leads`, {
        params: {
          company_id: company_id,
        },
      });
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing fetchCompanyLeads: ",
        error,
      );
      throw error;
    }
  },

  async fetchDealContacts(deal_id) {
    try {
      let res = await transport.get(BASE_PATH + "/api/deal_contacts", {
        params: {
          deal_id: deal_id,
        },
      });
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing fetchDealContacts: ",
        error,
      );
      throw error;
    }
  },

  async updateDealStage(deal_id, stage_id) {
    try {
      let res = await transport.post(BASE_PATH + "/api/deals/update_stage", {
        deal_id: deal_id,
        stage_id: stage_id,
      });
      return res.data;
    } catch (error) {
      if (error.code !== "ERR_CANCELED") {
        console.error(
          "An error occurred while executing updateDealStage: ",
          error,
        );
        throw error;
      }
    }
  },

  async checkPipelineStage(stage_id) {
    try {
      let res = await transport.post(BASE_PATH + "/api/pipelines/check_stage", {
        stage_id: stage_id,
      });
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing checkPipelineStage: ",
        error,
      );
      throw error;
    }
  },

  async deletePipelineStage(stage_id, new_stage_id) {
    try {
      let res = await transport.post(
        BASE_PATH + "/api/pipelines/delete_stage",
        {
          stage_id: stage_id,
          new_stage_id: new_stage_id,
        },
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing deletePipelineStage: ",
        error,
      );
      throw error;
    }
  },

  async fetchAllContacts() {
    try {
      let res = await transport.get(BASE_PATH + "/api/all_contacts", {
        params: {},
      });
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing fetchAllContacts: ",
        error,
      );
      throw error;
    }
  },

  async importViaCsvbox(
    entityType,
    importId,
    rowCount,
    entityId,
    source = null,
  ) {
    try {
      const payload = {
        entity_type: entityType,
        importId: importId,
        row_count: rowCount,
        entity_list_id: entityId,
      };

      if (source) {
        payload.source = source;
      }

      let res = await transport.post(BASE_PATH + "/api/csvbox/import", payload);
      return res.data;
    } catch (error) {
      console.error(`Error initiating ${entityType} import:`, error);
      throw error;
    }
  },

  async importViaCsvboxV2(payload) {
    try {
      let res = await transport.post(
        BASE_PATH + "/api/crm/v2/csvbox/import",
        payload,
      );
      return res.data;
    } catch (error) {
      console.error("Error initiating import v2:", error);
      throw error;
    }
  },

  async getObjectSchemaForCSVBox(objectType) {
    try {
      let res = await transport.get(
        BASE_PATH + `/api/crm/v2/csvbox/schema/${objectType}`,
      );
      return res.data;
    } catch (error) {
      console.error(`Error fetching schema for ${objectType}:`, error);
      throw error;
    }
  },

  async getImportSchema(entityType) {
    try {
      let res = await transport.get(
        BASE_PATH + `/api/csvbox/import_schema/${entityType}`,
      );
      return res.data;
    } catch (error) {
      console.error(`Error fetching import schema for ${entityType}:`, error);
      throw error;
    }
  },

  async exportCrmV2Data(params) {
    try {
      let res = await transport.post(
        BASE_PATH + "/api/crm/v2/csvbox/export",
        params,
      );
      return res.data;
    } catch (error) {
      console.error("Error initiating export:", error);
      throw error;
    }
  },

  async getExportDownloadUrl(exportId) {
    try {
      let res = await transport.get(
        BASE_PATH + `/api/crm/v2/csvbox/export/${exportId}/download`,
      );
      return res.data;
    } catch (error) {
      console.error(`Error fetching download URL for ${exportId}:`, error);
      throw error;
    }
  },

  async addCompany(companyData) {
    try {
      let res = await transport.post(BASE_PATH + "/api/companies", companyData);
      return res.data;
    } catch (error) {
      console.error("An error occurred while executing addCompany: ", error);
      throw error;
    }
  },

  async addContact(contactData) {
    try {
      let res = await transport.post(BASE_PATH + "/api/contacts", contactData);
      return res.data;
    } catch (error) {
      console.error("An error occurred while executing addContact: ", error);
      throw error;
    }
  },

  async addAsContactFromConversation(thread_id, email, type) {
    try {
      let res = await transport.post(
        BASE_PATH + "/api/conversations/add_as_contact",
        {
          thread_id: thread_id,
          email: email,
          reference_type: type,
        },
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing addAsContactFromConversation: ",
        error,
      );
      throw error;
    }
  },

  async addAsLeadFromConversation(thread_id, email, type) {
    try {
      let res = await transport.post(
        BASE_PATH + "/api/conversations/add_as_lead",
        {
          thread_id: thread_id,
          email: email,
          reference_type: type,
        },
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing addAsLeadFromConversation: ",
        error,
      );
      throw error;
    }
  },

  async logCallActivity(payload) {
    try {
      let res = await transport.post(BASE_PATH + "/api/calls", payload);
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing logCallActivity: ",
        error,
      );
      throw error;
    }
  },

  async logWhatsAppActivity(payload) {
    try {
      let res = await transport.post(BASE_PATH + "/api/whatsapp_logs", payload);
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing logWhatsAppActivity: ",
        error,
      );
      throw error;
    }
  },

  async logSmsActivity(payload) {
    try {
      let res = await transport.post(BASE_PATH + "/api/sms_logs", payload);
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing logSmsActivity: ",
        error,
      );
      throw error;
    }
  },

  async logLinkedInActivity(payload) {
    try {
      let res = await transport.post(BASE_PATH + "/api/linkedin_logs", payload);
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing logLinkedInActivity: ",
        error,
      );
      throw error;
    }
  },

  async meeting(payload) {
    try {
      let res = await transport.post(BASE_PATH + "/api/meetings", payload);
      return res.data;
    } catch (error) {
      console.error("An error occurred while executing meeting: ", error);
      throw error;
    }
  },

  async fetchLocationOptionsMeeting(payload = {}) {
    try {
      let res = await transport.post(
        `${BASE_PATH}/api/meeting_agent/meeting_vendors`,
        payload,
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing fetchLocationOptionsMeeting: ",
        error,
      );
      throw error;
    }
  },

  async verifyLocation(payload) {
    try {
      const res = await transport.post(
        `${BASE_PATH}/api/meeting_agent/verify_location`,
        payload,
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing verifyLocation: ",
        error,
      );
      throw error;
    }
  },

  async fetchMeetingDispositions(params = {}) {
    try {
      const res = await transport.get(`${BASE_PATH}/api/meeting_dispositions`, {
        params,
      });
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while fetching meeting dispositions: ",
        error,
      );
      throw error;
    }
  },

  async createMeetingDisposition(payload) {
    try {
      const res = await transport.post(
        `${BASE_PATH}/api/meeting_dispositions`,
        payload,
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while creating meeting disposition: ",
        error,
      );
      throw error;
    }
  },

  async updateMeetingDispositionSetting(dispositionId, payload) {
    try {
      const res = await transport.put(
        `${BASE_PATH}/api/meeting_dispositions/${dispositionId}`,
        payload,
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while updating meeting disposition setting: ",
        error,
      );
      throw error;
    }
  },

  async deleteMeetingDisposition(dispositionId) {
    try {
      const res = await transport.delete(
        `${BASE_PATH}/api/meeting_dispositions/${dispositionId}`,
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while deleting meeting disposition: ",
        error,
      );
      throw error;
    }
  },

  async fetchContactActivityLogs(
    contact_id,
    activity_type,
    page,
    activity_types_filter,
    created_by_id,
    created_date,
    form_ids,
  ) {
    try {
      let res = await transport.get(
        BASE_PATH + `/api/contacts/${contact_id}/activity_logs`,
        {
          params: {
            activity_type: activity_type,
            page: page,
            activity_types_filter,
            created_by_id,
            created_date,
            form_ids,
          },
        },
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing fetchContactActivityLogs: ",
        error,
      );
      throw error;
    }
  },

  async fetchDealActivityLogs(
    deal_id,
    activity_type,
    page,
    activity_types_filter,
    created_by_id,
    created_date,
    form_ids,
  ) {
    try {
      let res = await transport.get(
        BASE_PATH + `/api/deals/${deal_id}/activity_logs`,
        {
          params: {
            activity_type: activity_type,
            page: page,
            activity_types_filter,
            created_by_id,
            created_date,
            form_ids,
          },
        },
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing fetchDealActivityLogs: ",
        error,
      );
      throw error;
    }
  },

  async fetchCompanyActivityLogs(
    company_id,
    activity_type,
    page,
    activity_types_filter,
    created_by_id,
    created_date,
    form_ids,
  ) {
    try {
      let res = await transport.get(
        BASE_PATH + `/api/companies/${company_id}/activity_logs`,
        {
          params: {
            activity_type: activity_type,
            page: page,
            activity_types_filter,
            created_by_id,
            created_date,
            form_ids,
          },
        },
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing fetchCompanyActivityLogs: ",
        error,
      );
      throw error;
    }
  },

  async fetchLeadActivityLogs(
    lead_id,
    activity_type,
    page,
    activity_types_filter,
    created_by_id,
    created_date,
    form_ids,
  ) {
    try {
      let res = await transport.get(
        BASE_PATH + `/api/leads/${lead_id}/activity_logs`,
        {
          params: {
            activity_type: activity_type,
            page: page,
            activity_types_filter,
            created_by_id,
            created_date,
            form_ids,
          },
        },
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing fetchLeadActivityLogs: ",
        error,
      );
      throw error;
    }
  },

  async fetchCompanyEmployees(page, pageSize, companyWebsite, filters = {}) {
    try {
      const params = {
        page: page,
        page_size: pageSize,
        company_website: companyWebsite,
        search_query: filters.search_query || undefined,
        job_title: filters.job_title || undefined,
        department: filters.department || undefined,
        seniority: filters.seniority || undefined,
        location: filters.location || undefined,
      };

      let res = await transport.get(BASE_PATH + `/api/companies_employees`, {
        params: params,
      });
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing fetchCompanyEmployees: ",
        error,
      );
      throw error;
    }
  },

  async fetchEntityColleagues(
    page,
    pageSize,
    entityId,
    companyWebsite,
    source,
    filters = {},
  ) {
    try {
      const params = {
        page: page,
        page_size: pageSize,
        entity_id: entityId,
        company_website: companyWebsite,
        source: source,
        search_query: filters.search_query || undefined,
        job_title: filters.job_title || undefined,
        department: filters.department || undefined,
        seniority: filters.seniority || undefined,
        location: filters.location || undefined,
      };

      let res = await transport.get(BASE_PATH + `/api/entity_colleagues`, {
        params,
      });
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing fetchEntityColleagues: ",
        error,
      );
      throw error;
    }
  },

  async getTagsForContact() {
    try {
      let res = await transport.get(`${BASE_PATH}/api/tags/Contact`);
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing getTagsForContact: ",
        error,
      );
      throw error;
    }
  },

  async getTagsForContactFilters() {
    try {
      let res = await transport.get(`${BASE_PATH}/api/tags/Contact`, {
        params: { filter_options: "true" },
      });
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing getTagsForContactFilters: ",
        error,
      );
      throw error;
    }
  },

  async getTagsForLead() {
    try {
      let res = await transport.get(`${BASE_PATH}/api/tags/Lead`);
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing getTagsForLead: ",
        error,
      );
      throw error;
    }
  },

  async getTagsForLeadFilters() {
    try {
      let res = await transport.get(`${BASE_PATH}/api/tags/Lead`, {
        params: { filter_options: "true" },
      });
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing getTagsForLeadFilters: ",
        error,
      );
      throw error;
    }
  },

  async fetchCompanyLocation() {
    try {
      let res = await transport.get(
        `${BASE_PATH}/api/companies/generate_location`,
        {},
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing fetchCompanyLocation: ",
        error,
      );
      throw error;
    }
  },

  async getLeadSourceList() {
    try {
      let res = await transport.get(`${BASE_PATH}/api/lead_sources_list`, {});
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing getLeadSourceList: ",
        error,
      );
      throw error;
    }
  },

  async getLeadSourceOptions() {
    try {
      let res = await transport.get(`${BASE_PATH}/api/lead_source_options`);
      return res.data;
    } catch (error) {
      console.error("Error fetching lead source options:", error);
      throw error;
    }
  },

  async getTagsForDeal() {
    try {
      let res = await transport.get(`${BASE_PATH}/api/tags/Deal`);
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing getTagsForDeal: ",
        error,
      );
      throw error;
    }
  },

  async getTagsForDealFilters() {
    try {
      let res = await transport.get(`${BASE_PATH}/api/tags/Deal`, {
        params: { filter_options: "true" },
      });
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing getTagsForDealFilters: ",
        error,
      );
      throw error;
    }
  },

  async getTagsForCompany() {
    try {
      let res = await transport.get(`${BASE_PATH}/api/tags/Company`);
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing getTagsForCompany: ",
        error,
      );
      throw error;
    }
  },

  async getTagsForCompanyFilters() {
    try {
      let res = await transport.get(`${BASE_PATH}/api/tags/Company`, {
        params: { filter_options: "true" },
      });
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing getTagsForCompanyFilters: ",
        error,
      );
      throw error;
    }
  },

  async getTagsForMeeting() {
    try {
      const workspaceId = WorkspaceService.getCurrentWorkspaceId();
      const params = { workspace_id: workspaceId };
      let res = await transport.get(`${BASE_PATH}/api/tags/Meeting`, {
        params,
      });
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing getTagsForMeeting: ",
        error,
      );
      throw error;
    }
  },

  async getContact(id) {
    try {
      let res = await transport.get(`${BASE_PATH}/api/contacts/${id}`);
      return res.data;
    } catch (error) {
      console.error("An error occurred while executing getContact: ", error);
      throw error;
    }
  },

  async updateContact(id, contactData) {
    try {
      let res = await transport.put(
        `${BASE_PATH}/api/contacts/${id}`,
        contactData,
      );
      return res.data;
    } catch (error) {
      console.error("An error occurred while executing updateContact: ", error);
      throw error;
    }
  },

  async addExistingContacts(
    contactIds,
    companyId,
    dealId,
    actionId,
    listId,
    entityListId,
    taskId,
    meetingId,
  ) {
    try {
      let res = await transport.put(`${BASE_PATH}/api/add_existing_contacts`, {
        contact_ids: contactIds,
        company_id: companyId,
        deal_id: dealId,
        action_id: actionId,
        list_id: listId,
        entity_list_id: entityListId,
        task_id: taskId,
        meeting_id: meetingId,
      });
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing addExistingContacts: ",
        error,
      );
      throw error;
    }
  },

  async addExistingLeads(
    leadIds,
    companyId,
    dealId,
    actionId,
    listId,
    entityListId,
    taskId,
    meetingId,
  ) {
    try {
      let res = await transport.put(`${BASE_PATH}/api/add_existing_leads`, {
        lead_ids: leadIds,
        company_id: companyId,
        deal_id: dealId,
        action_id: actionId,
        list_id: listId,
        entity_list_id: entityListId,
        task_id: taskId,
        meeting_id: meetingId,
      });
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing addExistingLeads: ",
        error,
      );
      throw error;
    }
  },

  async addExistingCompanies(
    companyIds,
    contactId,
    dealId,
    actionId,
    leadId,
    entityListId,
    taskId,
    meetingId,
  ) {
    try {
      let res = await transport.put(`${BASE_PATH}/api/add_existing_companies`, {
        company_ids: companyIds,
        contact_id: contactId,
        deal_id: dealId,
        action_id: actionId,
        lead_id: leadId,
        entity_list_id: entityListId,
        task_id: taskId,
        meeting_id: meetingId,
      });
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing addExistingCompanies: ",
        error,
      );
      throw error;
    }
  },

  async addExistingDeals(payload) {
    try {
      let res = await transport.put(
        `${BASE_PATH}/api/add_existing_deals`,
        payload,
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing addExistingDeals: ",
        error,
      );
      throw error;
    }
  },

  async getCompany(id) {
    try {
      let res = await transport.get(`${BASE_PATH}/api/companies/${id}`);
      return res.data;
    } catch (error) {
      console.error("An error occurred while executing getCompany: ", error);
      throw error;
    }
  },

  async getProspectCompany(id) {
    try {
      let res = await transport.get(
        `${BASE_PATH}/api/prospect_companies/${id}`,
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing getProspectCompany: ",
        error,
      );
      throw error;
    }
  },

  async updateCompany(id, companyData) {
    try {
      let res = await transport.put(
        `${BASE_PATH}/api/companies/${id}`,
        companyData,
      );
      return res.data;
    } catch (error) {
      console.error("An error occurred while executing updateCompany: ", error);
      throw error;
    }
  },

  async deleteContact(id) {
    try {
      let res = await transport.delete(`${BASE_PATH}/api/contacts/${id}`);
      return res.data;
    } catch (error) {
      console.error("An error occurred while executing deleteContact: ", error);
      throw error;
    }
  },

  async deleteContacts(payload) {
    try {
      let res = await transport.delete(`${BASE_PATH}/api/contacts`, {
        data: payload,
      });
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing deleteContacts: ",
        error,
      );
      throw error;
    }
  },

  async deleteCompany(id) {
    try {
      let res = await transport.delete(`${BASE_PATH}/api/companies/${id}`);
      return res.data;
    } catch (error) {
      console.error("An error occurred while executing deleteCompany: ", error);
      throw error;
    }
  },

  async deleteCompanies(payload) {
    try {
      let res = await transport.delete(`${BASE_PATH}/api/companies`, {
        data: payload,
      });
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing deleteCompanies: ",
        error,
      );
      throw error;
    }
  },

  async getIndustries() {
    try {
      let res = await transport.get(`${BASE_PATH}/api/industries`);
      return res.data;
    } catch (error) {
      console.error("An error occurred while executing getIndustries: ", error);
      throw error;
    }
  },

  async allCompanies() {
    try {
      let res = await transport.get(`${BASE_PATH}/api/all_companies`);
      return res.data;
    } catch (error) {
      console.error("An error occurred while executing allCompanies: ", error);
      throw error;
    }
  },

  async addNote(contactId, content, attachmentIds = []) {
    try {
      const response = await transport.post(
        `${BASE_PATH}/api/contacts/${contactId}/notes`,
        { content: content, attachment_ids: attachmentIds },
      );
      return response.data;
    } catch (error) {
      console.error("Error adding note:", error);
      return { success: false, error };
    }
  },

  async addMultipleContactNotes(payload) {
    try {
      const response = await transport.post(
        `${BASE_PATH}/api/contacts/multiple/notes`,
        payload,
      );
      return response.data;
    } catch (error) {
      console.error(
        "An error occurred while executing addMultipleContactNotes: ",
        error,
      );
      throw error;
    }
  },

  async getContactNotes(contactId) {
    try {
      const response = await transport.get(
        `${BASE_PATH}/api/contacts/${contactId}/notes`,
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching notes:", error);
      return { success: false, error };
    }
  },

  async addCompaniesNote(companyId, content, attachmentIds = []) {
    try {
      const response = await transport.post(
        `${BASE_PATH}/api/companies/${companyId}/notes`,
        {
          content: content,
          attachment_ids: attachmentIds,
        },
      );
      return response.data;
    } catch (error) {
      console.error("Error adding note:", error);
      return { success: false, error };
    }
  },

  async addDealsNote(dealId, content, attachmentIds = []) {
    try {
      const response = await transport.post(
        `${BASE_PATH}/api/deals/${dealId}/notes`,
        { content: content, attachment_ids: attachmentIds },
      );
      return response.data;
    } catch (error) {
      console.error("Error adding note:", error);
      return { success: false, error };
    }
  },

  async addLeadsNote(leadId, content, attachmentIds = []) {
    try {
      const response = await transport.post(
        `${BASE_PATH}/api/leads/${leadId}/notes`,
        { content: content, attachment_ids: attachmentIds },
      );
      return response.data;
    } catch (error) {
      console.error("Error adding note:", error);
      return { success: false, error };
    }
  },

  async addMultipleLeadNotes(payload) {
    try {
      const response = await transport.post(
        `${BASE_PATH}/api/leads/multiple/notes`,
        payload,
      );
      return response.data;
    } catch (error) {
      console.error(
        "An error occurred while executing addMultipleLeadNotes: ",
        error,
      );
      throw error;
    }
  },

  async addMultipleCompanyNotes(payload) {
    try {
      const response = await transport.post(
        `${BASE_PATH}/api/companies/multiple/notes`,
        payload,
      );
      return response.data;
    } catch (error) {
      console.error(
        "An error occurred while executing addMultipleCompanyNotes: ",
        error,
      );
      throw error;
    }
  },

  async getCompanyNotes(companyId) {
    try {
      const response = await transport.get(
        `${BASE_PATH}/api/companies/${companyId}/notes`,
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching notes:", error);
      return { success: false, error };
    }
  },

  async getDealNotes(dealId) {
    try {
      const response = await transport.get(
        `${BASE_PATH}/api/deals/${dealId}/notes`,
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching notes:", error);
      return { success: false, error };
    }
  },

  async fetchLists(page, page_size, searchQuery = "") {
    try {
      let params = {
        page_size: page_size,
        page: page,
      };
      if (searchQuery) {
        params.search = searchQuery;
      }
      let res = await transport.get(BASE_PATH + "/api/lists", {
        params,
      });
      return res.data;
    } catch (error) {
      console.error("An error occurred while executing fetchLists: ", error);
      throw error;
    }
  },

  async deleteList(id) {
    try {
      let res = await transport.delete(`${BASE_PATH}/api/lists/${id}`);
      return res.data;
    } catch (error) {
      console.error("An error occurred while executing deleteList: ", error);
      throw error;
    }
  },

  async createList(listData) {
    try {
      let res = await transport.post(BASE_PATH + "/api/lists", listData);
      return res.data;
    } catch (error) {
      console.error("An error occurred while executing createList: ", error);
      throw error;
    }
  },

  async fetchListDetails(id) {
    try {
      let res = await transport.get(`${BASE_PATH}/api/lists/${id}`);
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing fetchListDetails: ",
        error,
      );
      throw error;
    }
  },

  async fetchListContacts(list_id, page, page_size) {
    try {
      let res = await transport.get(
        `${BASE_PATH}/api/list_contacts/${list_id}`,
        {
          params: {
            page_size: page_size,
            page: page,
          },
        },
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing fetchListContacts: ",
        error,
      );
      throw error;
    }
  },

  async removeContactFromList(listId, contactId) {
    try {
      let res = await transport.post(BASE_PATH + "/api/lists_contacts/remove", {
        list_id: listId,
        contact_id: contactId,
      });
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing removeContactFromList: ",
        error,
      );
      throw error;
    }
  },

  async fetchDeals(page, page_size, selectedPipelineId = null) {
    try {
      let res = await transport.get(BASE_PATH + "/api/deals", {
        params: {
          page_size: page_size,
          page: page,
          pipeline_id: selectedPipelineId,
        },
      });
      return res.data;
    } catch (error) {
      console.error("An error occurred while executing fetchDeals: ", error);
      throw error;
    }
  },

  async createDeal(dealData) {
    try {
      let res = await transport.post(BASE_PATH + "/api/deals", dealData);
      return res.data;
    } catch (error) {
      console.error("An error occurred while executing createDeal: ", error);
      throw error;
    }
  },

  async searchDeals(params) {
    try {
      let res = await transport.get(BASE_PATH + "/api/search_deals", {
        params: params,
      });
      return res.data;
    } catch (error) {
      console.error("An error occurred while executing searchDeals: ", error);
      throw error;
    }
  },

  async updateDeal(dealId, dealData) {
    try {
      let res = await transport.put(
        `${BASE_PATH}/api/deals/${dealId}`,
        dealData,
      );
      return res.data;
    } catch (error) {
      console.error("An error occurred while executing updateDeal: ", error);
      throw error;
    }
  },

  async fetchDealLatestActivity(dealId) {
    try {
      let res = await transport.get(
        `${BASE_PATH}/api/deals/${dealId}/latest_activity`,
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing fetchDealLatestActivity: ",
        error,
      );
      throw error;
    }
  },

  // Removed fetchDealsBatchLatestActivities - activity data is now included in deal responses

  async fetchDealDetails(id) {
    try {
      let res = await transport.get(`${BASE_PATH}/api/deals/${id}`);
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing fetchDealDetails: ",
        error,
      );
      throw error;
    }
  },

  async deleteDeal(dealId) {
    try {
      let res = await transport.delete(`${BASE_PATH}/api/deals/${dealId}`);
      return res.data;
    } catch (error) {
      console.error("An error occurred while executing deleteDeal: ", error);
      throw error;
    }
  },

  async deletePipeline(pipelineId) {
    try {
      let res = await transport.delete(
        `${BASE_PATH}/api/pipelines/${pipelineId}`,
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing deletePipeline: ",
        error,
      );
      throw error;
    }
  },

  async createPipeline(pipelineData) {
    try {
      let res = await transport.post(
        BASE_PATH + "/api/pipelines",
        pipelineData,
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing createPipeline: ",
        error,
      );
      throw error;
    }
  },

  async updatePipeline(pipelineId, pipelineData) {
    try {
      let res = await transport.put(
        `${BASE_PATH}/api/pipelines/${pipelineId}`,
        pipelineData,
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing updatePipeline: ",
        error,
      );
      throw error;
    }
  },

  async fetchPipelineDetails(id) {
    try {
      let res = await transport.get(`${BASE_PATH}/api/pipelines/${id}`);
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing fetchPipelineDetails: ",
        error,
      );
      throw error;
    }
  },

  async fetchDealPipelineView(id) {
    try {
      let res = await transport.get(`${BASE_PATH}/api/deal_pipeline/${id}`);
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing fetchDealPipelineView: ",
        error,
      );
      throw error;
    }
  },

  async fetchPipelines() {
    try {
      let res = await transport.get(BASE_PATH + "/api/pipelines", {
        params: {},
      });
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing fetchPipelines: ",
        error,
      );
      throw error;
    }
  },
  async toggleWeightedStats(enabled) {
    try {
      let res = await transport.post(
        BASE_PATH + "/api/pipelines/toggle_weighted_stats",
        { enabled },
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing toggleWeightedStats: ",
        error,
      );
      throw error;
    }
  },

  async getBusinessDetails() {
    try {
      let res = await transport.get(BASE_PATH + "/api/business", {
        params: {},
      });
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing getBusinessDetails: ",
        error,
      );
      throw error;
    }
  },
  async getWebsiteDomain() {
    try {
      let res = await transport.get(BASE_PATH + "/api/website_domain", {
        params: {},
      });
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing getWebsiteDomain: ",
        error,
      );
      throw error;
    }
  },
  async updateBusinessDetails(dealData) {
    try {
      let res = await transport.post(BASE_PATH + "/api/business", dealData);
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing updateBusinessDetails: ",
        error,
      );
      throw error;
    }
  },

  async uploadProfileImage(formstate) {
    try {
      let res;
      res = await transport.post(BASE_PATH + "/api/profile_image", formstate, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing uploadProfileImage: ",
        error,
      );
      throw error;
    }
  },

  async downloadProfileImage(businessId) {
    try {
      let res = await transport.get(BASE_PATH + "/api/profile_image/download", {
        params: {
          business_id: businessId,
        },
      });
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing downloadProfileImage: ",
        error,
      );
      throw error;
    }
  },

  async removeProfileImage(businessId) {
    try {
      let res = await transport.delete(
        `${BASE_PATH}/api/profile_image/delete`,
        {
          params: { business_id: businessId },
        },
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing removeProfileImage: ",
        error,
      );
      throw error;
    }
  },

  async startCompanyInsightsResearch(url) {
    try {
      let res = await transport.get("/api/start_company_insights", {
        params: { website: url },
      });
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing startCompanyInsightsResearch: ",
        error,
      );
      throw error;
    }
  },

  async getCompanyInsightsResearch(url) {
    try {
      let res = await transport.get(BASE_PATH + "/api/fetch_company_insights", {
        params: { website: url },
      });
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing getCompanyInsightsResearch: ",
        error,
      );
      throw error;
    }
  },

  async getIntegrations() {
    try {
      let res = await transport.get(BASE_PATH + "/api/integrations");
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing getIntegrations: ",
        error,
      );
      throw error;
    }
  },

  async getIntegrationDetails(name) {
    try {
      let res = await transport.get(`${BASE_PATH}/api/integrations/${name}`);
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing getIntegrationDetails: ",
        error,
      );
      throw error;
    }
  },

  async connectIntegration(
    integration_id,
    source = "/settings#integrations",
    api_key = null,
    is_team = false,
  ) {
    try {
      let payload = { source: source, is_team: is_team };
      if (api_key !== null) {
        payload = { ...payload, api_key: api_key };
      }
      let res = await transport.post(
        `${BASE_PATH}/api/connect_integrations/${integration_id}`,
        payload,
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing connectIntegration: ",
        error,
      );
      throw error;
    }
  },

  async searchSalesforceUsers(searchTerm) {
    try {
      let res = await transport.get(
        BASE_PATH + "/api/salesforce/search_users",
        {
          params: { search_term: searchTerm },
        },
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing searchSalesforceUsers: ",
        error,
      );
      throw error;
    }
  },

  async sendEmail(emailData) {
    try {
      if (emailData?.to?.length <= 0) {
        throw new Error("Please add at least one recipient");
      } else if (!emailData.subject) {
        throw new Error("Email subject cannot be empty");
      }

      const response = await transport.post(
        "/api/integrations/send_email",
        emailData,
      );

      return response.data;
    } catch (error) {
      console.error("An error occurred while executing sendEmail: ", error);
      throw error;
    }
  },

  // async fetchMailboxes(primary = false, shared = false) {
  //   let res = await transport.get(BASE_PATH + "/api/mailboxes/emails", {
  //     params: { primary: primary, shared: shared },
  //   });
  //   return res.data;
  // },

  async fetchPersonalMailboxes(primary = false) {
    try {
      let res = await transport.get(
        BASE_PATH + "/api/mailboxes/personal_emails",
        {
          params: { primary: primary },
        },
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing fetchPersonalMailboxes: ",
        error,
      );
      throw error;
    }
  },

  async fetchSharedMailboxes(params = {}) {
    try {
      let res = await transport.get(
        BASE_PATH + "/api/mailboxes/shared_emails",
        {
          params: params,
        },
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing fetchSharedMailboxes: ",
        error,
      );
      throw error;
    }
  },

  async fetchMailboxDetails(mailboxId) {
    try {
      let res = await transport.get(
        `${BASE_PATH}/api/mailboxes/${mailboxId}/details`,
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing fetchMailboxDetails: ",
        error,
      );
      throw error;
    }
  },

  async fetchMailboxSignatures() {
    try {
      let res = await transport.get(
        BASE_PATH + `/api/mailboxes/signatures`,
        {},
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing fetchMailboxSignatures: ",
        error,
      );
      throw error;
    }
  },

  async updateMailboxSettings(mailboxId, payload) {
    try {
      let res = await transport.post(
        `${BASE_PATH}/api/mailboxes/${mailboxId}/signature`,
        payload,
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing updateMailboxSettings: ",
        error,
      );
      throw error;
    }
  },

  async connectPersonalMailbox(vendor, primary = true) {
    try {
      let res = await transport.post(
        BASE_PATH + "/api/mailboxes/connect_personal_mailbox",
        {
          vendor: vendor,
          primary: primary,
        },
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing connectPersonalMailbox: ",
        error,
      );
      throw error;
    }
  },

  async connectSharedMailbox(vendor) {
    try {
      let res = await transport.post(
        BASE_PATH + "/api/mailboxes/connect_shared_mailbox",
        {
          vendor: vendor,
        },
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing connectSharedMailbox: ",
        error,
      );
      throw error;
    }
  },

  // async connectMailbox(vendor, primary = true) {
  //   let res = await transport.post(
  //     BASE_PATH + "/api/mailboxes/connect_mailbox",
  //     {
  //       vendor: vendor,
  //       primary: primary
  //     },
  //   );
  //   return res.data;
  // },

  async disconnectMailbox(payload) {
    try {
      let res = await transport.post(
        BASE_PATH + "/api/mailboxes/disconnect_mailbox",
        payload,
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing disconnectMailbox: ",
        error,
      );
      throw error;
    }
  },

  async deleteMailbox(emailAccountId) {
    try {
      let res = await transport.delete(
        BASE_PATH + `/api/mailboxes/${emailAccountId}`,
      );
      return res.data;
    } catch (error) {
      console.error("An error occurred while executing deleteMailbox: ", error);
      throw error;
    }
  },

  async deleteLinkedInAccount(linkedinAccountId) {
    try {
      let res = await transport.delete(
        BASE_PATH + `/api/linkedin/${linkedinAccountId}`,
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing deleteLinkedInAccount: ",
        error,
      );
      throw error;
    }
  },

  async sendLinkedInInvite(linkedinData) {
    try {
      let res = await transport.post(
        `${BASE_PATH}/api/send_linkedin_invite`,
        linkedinData,
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing sendLinkedInInvite: ",
        error,
      );
      throw error;
    }
  },

  async sendLinkedInMessage(linkedinData) {
    try {
      let res = await transport.post(
        `${BASE_PATH}/api/send_linkedin_message`,
        linkedinData,
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing sendLinkedInMessage: ",
        error,
      );
      throw error;
    }
  },

  async sendLinkedInMail(linkedinData) {
    try {
      let res = await transport.post(
        `${BASE_PATH}/api/send_linkedin_inmail`,
        linkedinData,
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing sendLinkedInMail: ",
        error,
      );
      throw error;
    }
  },

  async createEmailTemplate(templateForm) {
    try {
      let res = await transport.post(
        `${BASE_PATH}/api/email_templates`,
        templateForm,
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing createEmailTemplate: ",
        error,
      );
      throw error;
    }
  },

  async selectEmailTemplateForContact(templateId, toEmails, entityId, entityType) {
    try {
      let res = await transport.post(
        `${BASE_PATH}/api/email_templates/${templateId}/select_template_for_contact`,
        { to_emails: toEmails, entity_id: entityId, entity_type: entityType },
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing selectEmailTemplateForContact: ",
        error,
      );
      throw error;
    }
  },

  async updateEmailTemplate(templateId, templateForm) {
    try {
      let res = await transport.put(
        `${BASE_PATH}/api/email_templates/${templateId}`,
        templateForm,
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing updateEmailTemplate: ",
        error,
      );
      throw error;
    }
  },

  async deleteEmailTemplate(templateId) {
    try {
      let res = await transport.delete(
        `${BASE_PATH}/api/email_templates/${templateId}`,
        {},
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing deleteEmailTemplate: ",
        error,
      );
      throw error;
    }
  },

  async getEmailTemplates() {
    try {
      let res = await transport.get(BASE_PATH + "/api/email_templates", {
        params: {},
      });
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing getEmailTemplates: ",
        error,
      );
      throw error;
    }
  },

  async getTask(taskId) {
    try {
      const response = await transport.get(
        `${BASE_PATH}/api/tasks/${taskId}`,
        {},
      );
      return response.data;
    } catch (error) {
      console.error("An error occurred while executing getTask: ", error);
      throw error;
    }
  },

  async fetchProspectRecommendations(params) {
    const response = await transport.get(
      `${BASE_PATH}/api/prospect/recommendations`,
      {
        params: params,
      },
    );
    return response.data;
  },

  async bulkDeleteRecommendedLeads(payload) {
    try {
      const response = await transport.post(
        `${BASE_PATH}/api/recommend/leads/bulk_delete`,
        payload,
      );
      return response.data;
    } catch (error) {
      console.error(
        "An error occurred while deleting recommended leads: ",
        error,
      );
      throw error;
    }
  },

  async createTask(taskData) {
    try {
      const response = await transport.post(`${BASE_PATH}/api/tasks`, taskData);
      return response.data;
    } catch (error) {
      console.error("An error occurred while executing createTask: ", error);
      throw error;
    }
  },

  async updateTask(taskData) {
    try {
      const response = await transport.put(
        `${BASE_PATH}/api/tasks/${taskData.id}`,
        taskData,
      );
      return response.data;
    } catch (error) {
      console.error("An error occurred while executing updateTask: ", error);
      throw error;
    }
  },

  async deleteTask(taskId) {
    try {
      const response = await transport.delete(
        `${BASE_PATH}/api/tasks/${taskId}`,
      );
      return response.data;
    } catch (error) {
      console.error("An error occurred while executing deleteTask: ", error);
      throw error;
    }
  },

  async sendTeamInvites(payload) {
    try {
      let res = await transport.post(`${BASE_PATH}/api/teams/invites`, payload);
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing sendTeamInvites: ",
        error,
      );
      throw error;
    }
  },

  async removeUser(user_id) {
    try {
      let res = await transport.delete(
        BASE_PATH + `/api/teams/remove/${user_id}`,
      );
      return res.data;
    } catch (error) {
      console.error("An error occurred while executing removeUser: ", error);
      throw error;
    }
  },

  async removeUserInvite(payload) {
    try {
      let res = await transport.post(
        BASE_PATH + `/api/teams/delete_invite`,
        payload,
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing removeUserInvite: ",
        error,
      );
      throw error;
    }
  },

  async revokeUserInvite(payload) {
    try {
      let res = await transport.post(
        `${BASE_PATH}/api/teams/revoke_invite`,
        payload,
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing revokeUserInvite: ",
        error,
      );
      throw error;
    }
  },

  async getCompaniesByContactID(contactId) {
    try {
      const response = await transport.get(
        `${BASE_PATH}/api/companies/contact/${contactId}`,
      );
      return response.data;
    } catch (error) {
      console.error(
        "An error occurred while executing getCompaniesByContactID: ",
        error,
      );
      throw error;
    }
  },

  async getCompaniesByDealID(dealId) {
    try {
      const response = await transport.get(
        `${BASE_PATH}/api/deals/${dealId}/get_company`,
      );
      return response.data;
    } catch (error) {
      console.error(
        "An error occurred while executing getCompaniesByDealID: ",
        error,
      );
      throw error;
    }
  },

  async getLeadsByDealID(dealId) {
    try {
      const response = await transport.get(
        `${BASE_PATH}/api/deals/${dealId}/get_lead`,
      );
      return response.data;
    } catch (error) {
      console.error(
        "An error occurred while executing getLeadsByDealID: ",
        error,
      );
      throw error;
    }
  },

  async getDealsByContactID(contactId) {
    try {
      const response = await transport.get(
        `${BASE_PATH}/api/deals/contact/${contactId}`,
      );
      return response.data;
    } catch (error) {
      console.error(
        "An error occurred while executing getDealsByContactID: ",
        error,
      );
      throw error;
    }
  },

  async getListsByEntityId(entityType, entityId) {
    try {
      const res = await transport.get(
        `${BASE_PATH}/api/entity_lists/${entityType}/${entityId}`,
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing getListsByEntityId: ",
        error,
      );
      throw error;
    }
  },

  async getCompaniesByLeadID(leadId) {
    try {
      const response = await transport.get(
        `${BASE_PATH}/api/leads/${leadId}/get_company`,
      );
      return response.data;
    } catch (error) {
      console.error(
        "An error occurred while executing getCompaniesByLeadID: ",
        error,
      );
      throw error;
    }
  },

  async getDealsByLeadID(leadId) {
    try {
      const response = await transport.get(
        `${BASE_PATH}/api/deals/lead/${leadId}`,
      );
      return response.data;
    } catch (error) {
      console.error(
        "An error occurred while executing getDealsByLeadID: ",
        error,
      );
      throw error;
    }
  },

  async getDealsByCompanyID(companyId) {
    try {
      const response = await transport.get(
        `${BASE_PATH}/api/deals/company/${companyId}`,
      );
      return response.data;
    } catch (error) {
      console.error(
        "An error occurred while executing getDealsByCompanyID: ",
        error,
      );
      throw error;
    }
  },

  async fetchConversationThreads(page, search_query, filter, label, category) {
    try {
      let res = await transport.get(
        BASE_PATH + "/api/all_conversation_threads",
        {
          params: {
            page: page,
            search_query: search_query,
            filter: filter,
            label: label,
            category: category,
          },
        },
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing fetchConversationThreads: ",
        error,
      );
      throw error;
    }
  },

  async fetchConversation(thread_id, filter, label, category) {
    try {
      let res = await transport.get(
        BASE_PATH + `/api/conversations/${thread_id}/details`,
        {
          params: { filter: filter, label: label, category: category },
        },
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing fetchConversation: ",
        error,
      );
      throw error;
    }
  },

  async markConversationAsRead(thread_id) {
    try {
      let res = await transport.post(
        BASE_PATH + "/api/conversations/mark_as_read",
        {
          thread_id: thread_id,
        },
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing markConversationAsRead: ",
        error,
      );
      throw error;
    }
  },

  async fetchConversationThreadMessages(thread_id, page, page_size) {
    try {
      let res = await transport.get(BASE_PATH + "/api/conversation_messages", {
        params: {
          thread_id: thread_id,
          page_size: page_size,
          page: page,
        },
      });
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing fetchConversationThreadMessages: ",
        error,
      );
      throw error;
    }
  },

  async fetchContactDetailsForConversationThread(thread_id, contact_id) {
    try {
      let res = await transport.get(
        BASE_PATH + "/api/conversations/contact_details",
        {
          params: {
            thread_id: thread_id,
            contact_id: contact_id,
          },
        },
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing fetchContactDetailsForConversationThread: ",
        error,
      );
      throw error;
    }
  },

  async fetchConversationContacts(id) {
    try {
      let res = await transport.get(`/api/conversations/${id}/contacts`);

      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing fetchConversationContacts: ",
        error,
      );
      throw error;
    }
  },

  async enrichContactDetails(
    id,
    reveal_emails = false,
    reveal_phone_numbers = false,
  ) {
    try {
      let res = await transport.get(
        `${BASE_PATH}/api/v1/contact/${id}/enrich`,
        {
          params: {
            reveal_emails: reveal_emails,
            reveal_phone_numbers: reveal_phone_numbers,
          },
        },
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing enrichContactDetails: ",
        error,
      );
      throw error;
    }
  },

  // Async version of contact enrichment - uses MQTT for real-time updates
  async enrichContactAsync(id, params = {}) {
    try {
      const res = await transport.post(
        `${BASE_PATH}/api/v1/contact/${id}/enrich_async`,
        params,
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing enrichContactAsync: ",
        error,
      );
      throw error;
    }
  },

  async enrichCompanyDetails(id) {
    try {
      let res = await transport.get(`${BASE_PATH}/api/v1/company/${id}/enrich`);
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing enrichCompanyDetails: ",
        error,
      );
      throw error;
    }
  },

  async enrichLeadDetails(
    id,
    reveal_emails = false,
    reveal_phone_numbers = false,
  ) {
    try {
      let res = await transport.get(`${BASE_PATH}/api/v1/lead/${id}/enrich`, {
        params: {
          reveal_emails: reveal_emails,
          reveal_phone_numbers: reveal_phone_numbers,
        },
      });
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing enrichLeadDetails: ",
        error,
      );
      throw error;
    }
  },

  // Async version of lead enrichment - uses MQTT for real-time updates
  async enrichLeadAsync(id, params = {}) {
    try {
      const res = await transport.post(
        `${BASE_PATH}/api/v1/lead/${id}/enrich_async`,
        params,
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing enrichLeadAsync: ",
        error,
      );
      throw error;
    }
  },

  async enrichLeads(payload) {
    try {
      const response = await transport.post(
        `${BASE_PATH}/api/v1/leads/enrich`,
        payload,
      );
      return response.data;
    } catch (error) {
      console.error("An error occurred while executing enrichLeads: ", error);
      throw error;
    }
  },

  // Async version of bulk lead enrichment - uses MQTT for real-time updates
  async enrichLeadsAsync(params = {}) {
    try {
      const res = await transport.post(
        `${BASE_PATH}/api/v1/leads/enrich_async`,
        params,
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing enrichLeadsAsync: ",
        error,
      );
      throw error;
    }
  },

  async enrichContacts(payload) {
    try {
      const response = await transport.post(
        `${BASE_PATH}/api/v1/contacts/enrich`,
        payload,
      );
      return response.data;
    } catch (error) {
      console.error(
        "An error occurred while executing enrichContacts: ",
        error,
      );
      throw error;
    }
  },

  async enrichContactsAsync(payload) {
    try {
      const response = await transport.post(
        `${BASE_PATH}/api/v1/contacts/enrich_async`,
        payload,
      );
      return response.data;
    } catch (error) {
      console.error(
        "An error occurred while executing enrichContactsAsync: ",
        error,
      );
      throw error;
    }
  },

  async enrichCompanies(payload) {
    try {
      const response = await transport.post(
        `${BASE_PATH}/api/v1/companies/enrich`,
        payload,
      );
      return response.data;
    } catch (error) {
      console.error(
        "An error occurred while executing enrichCompanies: ",
        error,
      );
      throw error;
    }
  },

  async marketResearchContacts(payload) {
    try {
      const response = await transport.post(
        `${BASE_PATH}/api/research/contacts/initiate`,
        payload,
      );
      return response.data;
    } catch (error) {
      console.error(
        "An error occurred while executing marketResearchContacts: ",
        error,
      );
      throw error;
    }
  },

  async marketResearchLeads(payload) {
    try {
      const response = await transport.post(
        `${BASE_PATH}/api/research/leads/initiate`,
        payload,
      );
      return response.data;
    } catch (error) {
      console.error(
        "An error occurred while executing marketResearchLeads: ",
        error,
      );
      throw error;
    }
  },

  async marketResearchCompanies(payload) {
    try {
      const response = await transport.post(
        `${BASE_PATH}/api/research/companies/initiate`,
        payload,
      );
      return response.data;
    } catch (error) {
      console.error(
        "An error occurred while executing marketResearchCompanies: ",
        error,
      );
      throw error;
    }
  },

  async exportContacts(payload) {
    try {
      const response = await transport.post(
        `${BASE_PATH}/api/contacts/export`,
        payload,
      );
      return response.data;
    } catch (error) {
      console.error(
        "An error occurred while executing exportContacts: ",
        error,
      );
      throw error;
    }
  },

  async exportLeads(payload) {
    try {
      const response = await transport.post(
        `${BASE_PATH}/api/leads/export`,
        payload,
      );
      return response.data;
    } catch (error) {
      console.error("An error occurred while executing exportLeads: ", error);
      throw error;
    }
  },

  async exportCompanies(payload) {
    try {
      const response = await transport.post(
        `${BASE_PATH}/api/companies/export`,
        payload,
      );
      return response.data;
    } catch (error) {
      console.error(
        "An error occurred while executing exportCompanies: ",
        error,
      );
      throw error;
    }
  },

  async fetchRequestDetails(id) {
    try {
      let res = await transport.get(`${BASE_PATH}/api/request_details/${id}`);
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing fetchRequestDetails: ",
        error,
      );
      throw error;
    }
  },

  async checkPendingRequest(payload) {
    try {
      let res = await transport.post(
        `${BASE_PATH}/api/check_pending_request`,
        payload,
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing checkPendingRequest: ",
        error,
      );
      throw error;
    }
  },

  async getIntegrationsAPIKey(name) {
    try {
      let res = await transport.get(
        `${BASE_PATH}/api/integrations/api_key/${name}`,
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing getIntegrationsAPIKey: ",
        error,
      );
      throw error;
    }
  },

  async getRb2bWebhookUrl(name) {
    try {
      let res = await transport.get(
        `${BASE_PATH}/api/integrations/rb2b/webhook_url`,
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing getRb2bWebhookUrl: ",
        error,
      );
      throw error;
    }
  },

  async getWebhookUrl(name) {
    try {
      let res = await transport.get(
        `${BASE_PATH}/api/integrations/${name.toLowerCase()}/webhook_url`,
      );
      return res.data;
    } catch (error) {
      console.error("An error occurred while executing getWebhookUrl: ", error);
      throw error;
    }
  },

  async getSlackChannel(name) {
    try {
      let res = await transport.get(`${BASE_PATH}/api/slack/channel`);
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing getSlackChannel: ",
        error,
      );
      throw error;
    }
  },

  async getIntegrationKeys(name) {
    try {
      let res = await transport.get(
        `${BASE_PATH}/api/integrations/${name.toLowerCase()}/integration_keys`,
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing getIntegrationKeys: ",
        error,
      );
      throw error;
    }
  },

  async testIntegration(name) {
    try {
      let res = await transport.get(
        `${BASE_PATH}/api/integrations/${name.toLowerCase()}/test`,
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing testIntegration: ",
        error,
      );
      throw error;
    }
  },

  async updateIntegrationKeys(
    name,
    payload,
    source = "/settings#integrations",
    primary = true,
  ) {
    try {
      payload = { ...payload, source: source, primary: primary };
      let res = await transport.post(
        `${BASE_PATH}/api/integrations/${name.toLowerCase()}/connect`,
        payload,
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing updateIntegrationKeys: ",
        error,
      );
      throw error;
    }
  },

  async getSendsparkDynamicVideos() {
    try {
      let res = await transport.get(
        `${BASE_PATH}/api/integrations/sendspark/dynamic_campaigns`,
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing getSendsparkDynamicVideos: ",
        error,
      );
      throw error;
    }
  },

  async getIntegrationStatus(integrationId) {
    try {
      const response = await transport.get(
        `${BASE_PATH}/api/integrations/${integrationId}/status`,
      );
      return response.data;
    } catch (error) {
      console.error(
        "An error occurred while executing getIntegrationStatus: ",
        error,
      );
      throw error;
    }
  },

  async disconnectIntegration(integrationName) {
    try {
      const response = await transport.post(
        `${BASE_PATH}/api/integrations/${integrationName}/disconnect`,
      );
      return response.data;
    } catch (error) {
      console.error(
        "An error occurred while executing disconnectIntegration: ",
        error,
      );
      throw error;
    }
  },

  async getSalesforceDetails() {
    try {
      let res = await transport.get(
        BASE_PATH + `/api/integration/salesforce/details`,
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing getSalesforceDetails: ",
        error,
      );
      throw error;
    }
  },

  async getZohoDetails() {
    try {
      let res = await transport.get(
        BASE_PATH + `/api/integration/zoho/details`,
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing getZohoDetails: ",
        error,
      );
      throw error;
    }
  },

  async addLeadsFromFind(payload) {
    try {
      let res = await transport.post(`${BASE_PATH}/api/import/leads`, payload);
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing addLeadsFromFind: ",
        error,
      );
      throw error;
    }
  },

  async addLeadsFromFindAsync(payload) {
    try {
      let res = await transport.post(
        `${BASE_PATH}/api/import/leads_async`,
        payload,
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing addLeadsFromFind: ",
        error,
      );
      throw error;
    }
  },

  async enrichAndAddLead(payload) {
    try {
      let res = await transport.post(
        `${BASE_PATH}/api/enrich_and_add_lead`,
        payload,
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing enrichAndAddLead: ",
        error,
      );
      throw error;
    }
  },

  async enrichAndAddLeadAsync(payload) {
    try {
      let res = await transport.post(
        `${BASE_PATH}/api/enrich_and_add_lead_async`,
        payload,
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing enrichAndAddLead: ",
        error,
      );
      throw error;
    }
  },

  async enrichAndAddCompany(payload) {
    try {
      let res = await transport.post(
        `${BASE_PATH}/api/enrich_and_add_company`,
        payload,
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing enrichAndAddCompany: ",
        error,
      );
      throw error;
    }
  },

  async fetchLeads(page, page_size, search_query) {
    try {
      let res = await transport.get(BASE_PATH + "/api/leads", {
        params: {
          page_size: page_size,
          page: page,
          search_query: search_query,
        },
      });
      return res.data;
    } catch (error) {
      console.error("An error occurred while executing fetchLeads: ", error);
      throw error;
    }
  },

  async fetchAllLeads() {
    try {
      let res = await transport.get(BASE_PATH + "/api/all_leads", {
        params: {},
      });
      return res.data;
    } catch (error) {
      console.error("An error occurred while executing fetchAllLeads: ", error);
      throw error;
    }
  },

  async deleteLead(id) {
    try {
      let res = await transport.delete(`${BASE_PATH}/api/leads/${id}`);
      return res.data;
    } catch (error) {
      console.error("An error occurred while executing deleteLead: ", error);
      throw error;
    }
  },

  async deleteLeads(payload) {
    try {
      let res = await transport.delete(`${BASE_PATH}/api/leads`, {
        data: payload,
      });
      return res.data;
    } catch (error) {
      console.error("An error occurred while executing deleteLeads: ", error);
      throw error;
    }
  },

  async addLead(leadData) {
    try {
      let res = await transport.post(BASE_PATH + "/api/leads", leadData);
      return res.data;
    } catch (error) {
      console.error("An error occurred while executing addLead: ", error);
      throw error;
    }
  },

  async getLead(id) {
    try {
      let res = await transport.get(`${BASE_PATH}/api/leads/${id}`);
      return res.data;
    } catch (error) {
      console.error("An error occurred while executing getLead: ", error);
      throw error;
    }
  },

  async getProspectLead(id) {
    try {
      let res = await transport.get(`${BASE_PATH}/api/prospect_leads/${id}`);
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing getProspectLead: ",
        error,
      );
      throw error;
    }
  },

  async updateLead(id, leadData) {
    try {
      let res = await transport.put(`${BASE_PATH}/api/leads/${id}`, leadData);
      return res.data;
    } catch (error) {
      console.error("An error occurred while executing updateLead: ", error);
      throw error;
    }
  },

  async getLeadNotes(leadId) {
    try {
      const response = await transport.get(
        `${BASE_PATH}/api/leads/${leadId}/notes`,
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching notes:", error);
      return { success: false, error };
    }
  },

  async combinedSearch(query, parameter, page, limit, config = {}) {
    try {
      let res = await transport.post(
        BASE_PATH + "/api/search",
        {
          query: query,
          parameter: parameter,
          page: page,
          limit: limit,
        },
        config,
      );
      return res.data;
    } catch (error) {
      if (error.code !== "ERR_CANCELED") {
        console.error(
          "An error occurred while executing combinedSearch: ",
          error,
        );
        throw error;
      }
    }
  },

  async fetchEntityViews(endpoint) {
    try {
      let res = await transport.get(`${BASE_PATH}/api/${endpoint}/views`);
      return res.data;
    } catch (error) {
      console.error(
        `An error occurred while executing fetchEntityViews for ${endpoint}: `,
        error,
      );
      throw error;
    }
  },

  async fetchContactViews() {
    try {
      let res = await transport.get(`${BASE_PATH}/api/contact/views`);
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing fetchContactViews: ",
        error,
      );
      throw error;
    }
  },

  async fetchDocumentViews() {
    try {
      let res = await transport.get(`${BASE_PATH}/api/document/views`);
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing fetchDocumentViews: ",
        error,
      );
      throw error;
    }
  },

  async fetchLeadsViews() {
    try {
      let res = await transport.get(`${BASE_PATH}/api/lead/views`);
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing fetchLeadsViews: ",
        error,
      );
      throw error;
    }
  },

  async fetchMeetingLogViews() {
    try {
      let res = await transport.get(`${BASE_PATH}/api/meeting/views`);
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing fetchMeetingLogViews: ",
        error,
      );
      throw error;
    }
  },

  async fetchDealsViews() {
    try {
      let res = await transport.get(`${BASE_PATH}/api/deal/views`);
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing fetchDealsViews: ",
        error,
      );
      throw error;
    }
  },

  async fetchEntityListsViews() {
    try {
      let res = await transport.get(`${BASE_PATH}/api/entity_list/views`);
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing fetchEntityListsViews: ",
        error,
      );
      throw error;
    }
  },

  async fetchCampaignsInEngageViews() {
    try {
      let res = await transport.get(
        `${BASE_PATH}/api/campaigns_in_engage/views`,
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing fetchCampaignsInEngageViews: ",
        error,
      );
      throw error;
    }
  },

  async fetchSandboxViews() {
    try {
      let res = await transport.get(`${BASE_PATH}/api/sandbox/views`);
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing fetchSandboxViews: ",
        error,
      );
      throw error;
    }
  },

  async fetchListViews(id) {
    try {
      let res = await transport.get(`${BASE_PATH}/api/list/views/${id}`);
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing fetchListViews: ",
        error,
      );
      throw error;
    }
  },

  async applyFilter(view_id, payload) {
    try {
      let res = await transport.put(
        `${BASE_PATH}/api/views/${view_id}`,
        payload,
      );

      return res.data;
    } catch (error) {
      console.error("[FILTER_DEBUG] Error in applyFilter:", error);
      throw error;
    }
  },

  async updateTaskStatus(task_id, payload) {
    try {
      let res = await transport.put(
        `${BASE_PATH}/api/task/status/${task_id}`,
        payload,
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing updateTaskStatus: ",
        error,
      );
      throw error;
    }
  },

  async getFilteredData(
    view_type,
    view_id,
    page,
    page_size,
    search_query,
    additionalParams = {},
  ) {
    try {
      let res = await transport.get(
        `${BASE_PATH}/api/views/${view_type}/${view_id}/results`,
        {
          params: {
            page_size: page_size,
            page: page,
            search_query: search_query,
            ...additionalParams,
          },
        },
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing getFilteredData: ",
        error,
      );
      throw error;
    }
  },

  async getFilteredListEntities(listId, page, pageSize, searchQuery, viewId, lastDocumentId = null, firstDocumentId = null) {
    try {
      const params = {
        page_size: pageSize,
        page: page,
        search_query: searchQuery,
        view_id: viewId,
      };

      if (lastDocumentId) {
        params.last_document_id = lastDocumentId;
      }

      if (firstDocumentId) {
        params.first_document_id = firstDocumentId;
      }

      let res = await transport.get(
        `${BASE_PATH}/api/entity_lists/${listId}/results`,
        { params },
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing getFilteredListEntities: ",
        error,
      );
      throw error;
    }
  },

  async getFilteredDeals(
    view_id,
    page,
    page_size,
    pipeline_id,
    search_query,
    is_kanban = false,
  ) {
    try {
      const params = {
        page_size,
        page,
        search_query,
        is_kanban,
      };

      if (pipeline_id != null) {
        params.pipeline_id = pipeline_id;
      }

      let res = await transport.get(
        `${BASE_PATH}/api/views/deals/${view_id}/results`,
        {
          params,
        },
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing getFilteredDeals: ",
        error,
      );
      throw error;
    }
  },

  async fetchCompanyViews() {
    try {
      let res = await transport.get(`${BASE_PATH}/api/company/views`);
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing fetchCompanyViews: ",
        error,
      );
      throw error;
    }
  },

  async fetchCustomerViews() {
    try {
      let res = await transport.get(`${BASE_PATH}/api/customer/views`);
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing fetchCustomerViews: ",
        error,
      );
      throw error;
    }
  },

  async checkCompanyWebsite(companyId) {
    try {
      let res = await transport.get(
        `${BASE_PATH}/api/research/company/verify_website/${companyId}`,
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing checkCompanyWebsite: ",
        error,
      );
      throw error;
    }
  },

  async fetchResearchPreview(researchId) {
    try {
      let res = await transport.get(
        `${BASE_PATH}/api/research/preview/${researchId}`,
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing fetchResearchPreview: ",
        error,
      );
      throw error;
    }
  },
  async startCompanyResearch(companyId, userInstruction = "") {
    try {
      let res = await transport.post(
        `${BASE_PATH}/api/research/company/initiate/${companyId}`,
        { user_instruction: userInstruction },
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing startCompanyResearch: ",
        error,
      );
      throw error;
    }
  },
  async fetchCompanyResearchDetails(companyId) {
    try {
      let res = await transport.get(
        `${BASE_PATH}/api/research/company/${companyId}`,
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing fetchCompanyResearchDetails: ",
        error,
      );
      throw error;
    }
  },

  async checkContactCompany(contactId) {
    try {
      let res = await transport.get(
        `${BASE_PATH}/api/research/contact/verify_company/${contactId}`,
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing checkContactCompany: ",
        error,
      );
      throw error;
    }
  },

  async startContactResearch(contactId, userInstruction = "") {
    try {
      let res = await transport.post(
        `${BASE_PATH}/api/research/contact/initiate/${contactId}`,
        { user_instruction: userInstruction },
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing startContactResearch: ",
        error,
      );
      throw error;
    }
  },

  async fetchPendingContactResearch() {
    try {
      let res = await transport.get(
        `${BASE_PATH}/api/research/contact/pending_contact_research`,
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing fetchPendingContactResearch: ",
        error,
      );
      throw error;
    }
  },

  async fetchPendingLeadResearch() {
    try {
      let res = await transport.get(
        `${BASE_PATH}/api/research/lead/pending_lead_research`,
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing fetchPendingLeadResearch: ",
        error,
      );
      throw error;
    }
  },

  async fetchContactResearchDetails(contactId) {
    try {
      let res = await transport.get(
        `${BASE_PATH}/api/research/contact/${contactId}`,
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing fetchContactResearchDetails: ",
        error,
      );
      throw error;
    }
  },

  async checkLeadCompany(leadId) {
    try {
      let res = await transport.get(
        `${BASE_PATH}/api/research/lead/verify_company/${leadId}`,
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing checkLeadCompany: ",
        error,
      );
      throw error;
    }
  },

  async startLeadResearch(leadId, userInstruction = "") {
    try {
      let res = await transport.post(
        `${BASE_PATH}/api/research/lead/initiate/${leadId}`,
        { user_instruction: userInstruction },
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing startLeadResearch: ",
        error,
      );
      throw error;
    }
  },

  async fetchLeadResearchDetails(leadId) {
    try {
      let res = await transport.get(`${BASE_PATH}/api/research/lead/${leadId}`);
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing fetchLeadResearchDetails: ",
        error,
      );
      throw error;
    }
  },

  async fetchTaskViews() {
    try {
      let res = await transport.get(`${BASE_PATH}/api/task/views`);
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing fetchTaskViews: ",
        error,
      );
      throw error;
    }
  },

  async getPipelineStages(pipeline_id) {
    try {
      let res = await transport.get(`${BASE_PATH}/api/pipelines_stages`, {
        params: {
          pipeline_id: pipeline_id,
        },
      });
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing getPipelineStages: ",
        error,
      );
      throw error;
    }
  },

  async getPipelineStagesBatch(pipeline_ids = []) {
    try {
      let res = await transport.post(
        `${BASE_PATH}/api/pipelines_stages/batch`,
        {
          pipeline_ids: pipeline_ids,
        },
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing getPipelineStagesBatch: ",
        error,
      );
      throw error;
    }
  },

  async fetchEnabledFeatures() {
    try {
      let res = await transport.get(`${BASE_PATH}/api/features`);
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing fetchEnabledFeatures: ",
        error,
      );
      throw error;
    }
  },

  async fetchSenderDetails() {
    try {
      let res = await transport.get(
        `${BASE_PATH}/api/integrations/send_details`,
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing fetchSenderDetails: ",
        error,
      );
      throw error;
    }
  },

  async getAgiMessages(page, page_size) {
    try {
      let res = await transport.get(BASE_PATH + `/api/ai-chat/get_messages`, {
        params: {
          page: page,
          page_size: page_size,
        },
      });
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing getAgiMessages: ",
        error,
      );
      throw error;
    }
  },

  async sendAgiMessage(question, current_url = null) {
    try {
      const payload = { question: question };
      if (current_url) {
        payload.current_url = current_url;
      }

      let res = await transport.post(
        BASE_PATH + "/api/ai-chat/post_message",
        payload,
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing sendAgiMessage: ",
        error,
      );
      throw error;
    }
  },

  async sendAgiMessageStreaming(
    question,
    conversationId,
    currentUrl = null,
    objectType = null,
    objectId = null,
  ) {
    try {
      const payload = {
        question: question,
        conversation_id: conversationId,
        stream: true,
      };
      if (currentUrl) {
        payload.current_url = currentUrl;
      }
      if (objectType) {
        payload.object_type = objectType;
      }
      if (objectId) {
        payload.object_id = objectId;
      }

      let res = await transport.post(
        BASE_PATH + "/api/ai-chat/post_message_streaming",
        payload,
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing sendAgiMessageStreaming: ",
        error,
      );
      throw error;
    }
  },

  async saveAgiMessage(
    conversationId,
    turnId,
    message,
    jsonData = null,
    recommendedQuestions = null,
    images = null,
    webSearchUsed = null,
    direction = null,
  ) {
    try {
      const payload = {
        conversation_id: conversationId,
        turn_id: turnId,
        message: message,
      };
      if (jsonData) payload.json_data = jsonData;
      if (recommendedQuestions)
        payload.recommended_questions = recommendedQuestions;
      if (images) payload.images = images;
      if (webSearchUsed !== null) payload.web_search_used = webSearchUsed;
      if (direction) payload.direction = direction;

      let res = await transport.post(
        BASE_PATH + "/api/ai-chat/save_message",
        payload,
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing saveAgiMessage: ",
        error,
      );
      throw error;
    }
  },

  getSSEUrl(conversationId) {
    return `${BASE_PATH}/api/ai-chat/sse?conversation_id=${conversationId}`;
  },

  async getTwinStatus() {
    try {
      let res = await transport.get(BASE_PATH + "/api/ai-chat/twin_status");
      return res.data;
    } catch (error) {
      console.error("An error occurred while fetching twin status:", error);
      return { has_digital_twin: false };
    }
  },

  async fetchComments(entity_type, entity_id, page = 0, page_size = 10) {
    try {
      let res = await transport.get(BASE_PATH + "/api/comments", {
        params: {
          entity_type: entity_type,
          entity_id: entity_id,
          page: page,
          page_size: page_size,
        },
      });
      return res.data;
    } catch (error) {
      console.error("An error occurred while executing fetchComments: ", error);
      throw error;
    }
  },

  async postComments(payload) {
    try {
      let res = await transport.post(BASE_PATH + "/api/comments", payload);
      return res.data;
    } catch (error) {
      console.error("An error occurred while executing postComments: ", error);
      throw error;
    }
  },

  async fetchNotifications() {
    try {
      let res = await transport.get(BASE_PATH + "/api/notifications");
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing fetchNotifications: ",
        error,
      );
      throw error;
    }
  },

  async markNotificationAsRead(ids) {
    try {
      let res = await transport.put(
        BASE_PATH + "/api/notifications/mark_read",
        {
          notification_ids: ids,
        },
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing markNotificationAsRead: ",
        error,
      );
      throw error;
    }
  },

  async fetchMqttCredentials() {
    try {
      let res = await transport.get(BASE_PATH + "/api/mqtt/creds");
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing fetchMqttCredentials: ",
        error,
      );
      throw error;
    }
  },

  async uploadAttachment(formData) {
    try {
      let res = await transport.post(
        "/api/conversation_attachments",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing uploadAttachment: ",
        error,
      );
      throw error;
    }
  },

  async uploadAttachmentV2(formData) {
    try {
      let res = await transport.post("/api/attachments", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing uploadAttachmentV2: ",
        error,
      );
      throw error;
    }
  },

  async uploadMarketingFile(formData) {
    try {
      let res = await marketingTransport.post("/marketing/uploads", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing uploadMarketingFile: ",
        error,
      );
      throw error;
    }
  },

  /* --------------- External Group Invites --------------- */

  async validateExternalGroupInvite(token) {
    try {
      let res = await transport.get(BASE_PATH + `/api/external/groups/invite/${token}`);
      return res.data;
    } catch (error) {
      console.error("An error occurred while validating external group invite: ", error);
      throw error;
    }
  },

  async registerExternalInviteToken(inviteToken, workspaceId, groupId) {
    try {
      let res = await transport.post(BASE_PATH + `/api/external/groups/register-token`, {
        invite_token: inviteToken,
        workspace_id: workspaceId,
        group_id: groupId,
      });
      return res.data;
    } catch (error) {
      console.error("An error occurred while registering invite token: ", error);
    }
  },

  async joinExternalGroup(token, memberWorkspaceId) {
    try {
      let res = await transport.post(BASE_PATH + `/api/external/groups/join`, {
        token,
        member_workspace_id: memberWorkspaceId,
      });
      return res.data;
    } catch (error) {
      console.error("An error occurred while joining external group: ", error);
      throw error;
    }
  },

  async inviteExternalGroupByEmail(groupId, emails) {
    try {
      let res = await transport.post(BASE_PATH + `/api/external/groups/invite-by-email`, {
        group_id: groupId,
        emails: emails,
      });
      return res.data;
    } catch (error) {
      console.error("An error occurred while inviting emails to external group: ", error);
      throw error;
    }
  },

  async fetchExternalGroupPendingInvites(groupId) {
    try {
      let res = await transport.get(BASE_PATH + `/api/external/groups/${groupId}/pending-invites`);
      return res.data;
    } catch (error) {
      console.error("An error occurred while fetching pending invites: ", error);
      throw error;
    }
  },

  async revokeExternalGroupInvite(groupId, email) {
    try {
      let res = await transport.post(BASE_PATH + `/api/external/groups/${groupId}/revoke-invite`, {
        email,
      });
      return res.data;
    } catch (error) {
      console.error("An error occurred while revoking invite: ", error);
      throw error;
    }
  },

  async notifyExternalGroupPendingInvitees(groupId, messageText) {
    try {
      let res = await transport.post(BASE_PATH + `/api/external/groups/${groupId}/notify-pending`, {
        message_text: messageText,
      });
      return res.data;
    } catch (error) {
      console.error("An error occurred while notifying pending invitees: ", error);
      throw error;
    }
  },

  /* --------------- Saved Views --------------- */

  async updateView(view_id, data) {
    try {
      let res = await transport.put(BASE_PATH + `/api/views/${view_id}`, data);
      return res.data;
    } catch (error) {
      console.error("An error occurred while executing updateView: ", error);
      throw error;
    }
  },

  async deleteView(view_id, service = null) {
    try {
      const params = service ? { service } : {};
      let res = await viewsTransport.delete(`/views/${view_id}`, { params });
      return res.data;
    } catch (error) {
      console.error("An error occurred while executing deleteView: ", error);
      throw error;
    }
  },

  async createNewView(data) {
    try {
      let res = await viewsTransport.post(`/views`, data);
      return res.data;
    } catch (error) {
      console.error("An error occurred while executing createNewView: ", error);
      throw error;
    }
  },

  async updateViewSequence(data) {
    try {
      let res = await transport.put(
        BASE_PATH + `/api/views/active_views`,
        data,
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing updateViewSequence: ",
        error,
      );
      throw error;
    }
  },

  async updateCurrentView(view_id) {
    try {
      let res = await transport.put(
        BASE_PATH + `/api/views/${view_id}/current_view`,
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing updateCurrentView: ",
        error,
      );
      throw error;
    }
  },

  async closeView(view_id) {
    try {
      let res = await transport.put(BASE_PATH + `/api/views/${view_id}/close`);
      return res.data;
    } catch (error) {
      console.error("An error occurred while executing closeView: ", error);
      throw error;
    }
  },

  async fetchViewSharingDetails(view_id) {
    try {
      let res = await transport.get(
        BASE_PATH + `/api/views/${view_id}/sharing`,
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing fetchViewSharingDetails: ",
        error,
      );
      throw error;
    }
  },

  /* --------------- Find Contacts and Companies APIs --------------- */
  async findContacts(payload) {
    try {
      let res = await transport.post(
        `${BASE_PATH}/api/contacts/discover`,
        payload,
      );
      return res.data;
    } catch (error) {
      console.error("An error occurred while executing findContacts: ", error);
      throw error;
    }
  },

  async addContactsFromFind(payload) {
    try {
      let res = await transport.post(
        `${BASE_PATH}/api/import/contacts`,
        payload,
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing addContactsFromFind: ",
        error,
      );
      throw error;
    }
  },

  async addCompaniesFromFind(payload) {
    try {
      let res = await transport.post(
        `${BASE_PATH}/api/import/companies`,
        payload,
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing addCompaniesFromFind: ",
        error,
      );
      throw error;
    }
  },

  /* --------------- Content Management APIs --------------- */

  async allDocuments() {
    try {
      let res = await transport.get(`${BASE_PATH}/api/documents`);
      return res.data;
    } catch (error) {
      console.error("An error occurred while executing allDocuments: ", error);
      throw error;
    }
  },

  async getDocumentById(document_id) {
    try {
      let res = await transport.get(
        `${BASE_PATH}/api/documents/${document_id}`,
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing getDocumentById: ",
        error,
      );
      throw error;
    }
  },

  async deleteDocument(documentId) {
    try {
      let res = await transport.delete(
        `${BASE_PATH}/api/documents/${documentId}`,
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing deleteDocument: ",
        error,
      );
      throw error;
    }
  },

  async createDocument(payload) {
    try {
      let res = await transport.post(
        `${BASE_PATH}/api/documents/create`,
        payload,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing createDocument: ",
        error,
      );
      throw error;
    }
  },

  async updateDocument(documentId, payload) {
    try {
      let res = await transport.post(
        `${BASE_PATH}/api/documents/update/${documentId}`,
        payload,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing updateDocument: ",
        error,
      );
      throw error;
    }
  },

  async createDocumentShareLink(payload) {
    try {
      let res = await transport.post(
        `${BASE_PATH}/api/document_links`,
        payload,
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing createDocumentShareLink: ",
        error,
      );
      throw error;
    }
  },

  async getAllDownloadLogs(document_id) {
    try {
      let res = await transport.get(
        `${BASE_PATH}/api/document_download_log/${document_id}`,
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing getAllDownloadLogs: ",
        error,
      );
      throw error;
    }
  },

  /* --------------- AUTH USER APIs --------------- */

  async getOnboardingStatus() {
    try {
      let res = await transport.get(`${BASE_PATH}/api/auth/onboarding_status`);
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing getOnboardingStatus: ",
        error,
      );
      throw error;
    }
  },

  async updateOnboardingStatus(status) {
    try {
      let res = await transport.put(`${BASE_PATH}/api/auth/onboarding_status`, {
        status: status,
      });
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing updateOnboardingStatus: ",
        error,
      );
      throw error;
    }
  },

  /* --------------- Entity List APIs --------------- */

  async getAllEntityLists(source) {
    try {
      let res = await transport.get(`${BASE_PATH}/api/entity_lists/${source}`);
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing getAllEntityLists: ",
        error,
      );
      throw error;
    }
  },

  async getAllEntityListsPaginated(source, page = 0, pageSize = 20, searchQuery = "") {
    try {
      let res = await transport.get(`${BASE_PATH}/api/entity_lists/${source}`, {
        params: {
          paginated: true,
          page,
          page_size: pageSize,
          search_query: searchQuery || undefined,
        },
      });
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing getAllEntityListsPaginated: ",
        error,
      );
      throw error;
    }
  },

  async getAllEntityListsBySource(params) {
    try {
      let res = await transport.get(`${BASE_PATH}/api/entity_lists`, {
        params: params,
      });
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing getAllEntityListsBySource: ",
        error,
      );
      throw error;
    }
  },

  async checkEntityListImportProgress(listId) {
    try {
      let res = await transport.get(
        `${BASE_PATH}/api/entity_lists/${listId}/import_progress`,
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing checkEntityListImportProgress: ",
        error,
      );
      throw error;
    }
  },

  async getEntityListByID(listID, page_number, page_size) {
    try {
      let res = await transport.get(
        `${BASE_PATH}/api/entity_lists/id/${listID}`,
        {
          params: {
            page_number: page_number,
            page_size: page_size,
          },
        },
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing getEntityListByID: ",
        error,
      );
      throw error;
    }
  },

  async updateEntityListSettings(id, payload) {
    try {
      let res = await transport.post(
        `${BASE_PATH}/api/entity_lists/${id}/update_list_settings`,
        payload,
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing updateEntityListSettings: ",
        error,
      );
      throw error;
    }
  },

  async createEntityList(payload) {
    try {
      let res = await transport.post(
        `${BASE_PATH}/api/entity_lists/create`,
        payload,
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing createEntityList: ",
        error,
      );
      throw error;
    }
  },

  async deleteEntityList(listID) {
    try {
      let res = await transport.delete(
        `${BASE_PATH}/api/entity_lists/delete/${listID}`,
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing deleteEntityList: ",
        error,
      );
      throw error;
    }
  },

  async addEntityLists(payload) {
    try {
      let res = await transport.post(
        `${BASE_PATH}/api/entity_lists/add`,
        payload,
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing addEntityLists: ",
        error,
      );
      throw error;
    }
  },

  async addEntityToList(entityType, payload) {
    try {
      const res = await transport.post(
        `${BASE_PATH}/api/entity_lists/add_entity`,
        payload,
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing addEntityToList: ",
        error,
      );
      throw error;
    }
  },

  async addEntityToSequence(entityType, payload) {
    try {
      const res = await transport.post(
        `${BASE_PATH}/api/sequence/add_entity`,
        payload,
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing addEntityToSequence: ",
        error,
      );
      throw error;
    }
  },

  async addListToSequence(payload) {
    try {
      const res = await transport.post(
        `${BASE_PATH}/api/entity_lists/add_list_to_sequence`,
        payload,
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing addListToSequence: ",
        error,
      );
      throw error;
    }
  },

  async removeEntityListItems(payload) {
    try {
      let res = await transport.post(
        `${BASE_PATH}/api/entity_lists/delete`,
        payload,
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing removeEntityListItems: ",
        error,
      );
      throw error;
    }
  },

  async getUnassociatedListsByEntityId(entityType, entityId) {
    try {
      const res = await transport.get(
        `${BASE_PATH}/api/unassociated_entity_lists/${entityType}/${entityId}`,
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing getUnassociatedListsByEntityId: ",
        error,
      );
      throw error;
    }
  },

  async getEntityListV3() {
    try {
      let res = await transport.get(`${BASE_PATH}/api/new_entity_lists`);
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing getEntityListV3: ",
        error,
      );
      throw error;
    }
  },
  /* --------------- HOME APIs --------------- */

  async fetchHomeEntities() {
    try {
      let res = await transport.get(`${BASE_PATH}/api/home/entities_for_home`);
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing fetchHomeEntities: ",
        error,
      );
      throw error;
    }
  },

  async fetchHomeActivities() {
    try {
      let res = await transport.get(`${BASE_PATH}/api/home/activities`);
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing fetchHomeActivities: ",
        error,
      );
      throw error;
    }
  },

  async fetchHomeAnalytics() {
    try {
      let res = await transport.get(`${BASE_PATH}/api/home/analytics`);
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing fetchHomeAnalytics: ",
        error,
      );
      throw error;
    }
  },

  /* --------------- GET STARTED APIs --------------- */

  async fetchGetStartedSteps() {
    try {
      let res = await transport.get(`${BASE_PATH}/api/get_started/steps`);
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing fetchGetStartedSteps: ",
        error,
      );
      throw error;
    }
  },

  async fetchGetStartedStatus() {
    try {
      let res = await transport.get(`${BASE_PATH}/api/get_started/status`);
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing fetchGetStartedStatus: ",
        error,
      );
      throw error;
    }
  },

  /* --------------- Meeting Agent APIs --------------- */

  async fetchMeetingAgents(params = {}) {
    try {
      let res = await transport.get(`${BASE_PATH}/api/meeting_agent`, {
        params: params,
      });
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing fetchMeetingAgents: ",
        error,
      );
      throw error;
    }
  },

  async getMeetingAgentDetails(agentId) {
    try {
      const response = await transport.get(
        `${BASE_PATH}/api/meeting_agent/${agentId}`,
        {},
      );
      return response.data;
    } catch (error) {
      console.error(
        "An error occurred while executing getMeetingAgentDetails: ",
        error,
      );
      throw error;
    }
  },

  async createMeetingAgent(agentData) {
    try {
      const response = await transport.post(
        `${BASE_PATH}/api/meeting_agent`,
        agentData,
      );
      return response.data;
    } catch (error) {
      console.error(
        "An error occurred while executing createMeetingAgent: ",
        error,
      );
      throw error;
    }
  },

  async updateMeetingAgent(agentData) {
    try {
      const response = await transport.put(
        `${BASE_PATH}/api/meeting_agent/${agentData.id}`,
        agentData,
      );
      return response.data;
    } catch (error) {
      console.error(
        "An error occurred while executing updateMeetingAgent: ",
        error,
      );
      throw error;
    }
  },

  async deleteMeetingAgent(agentId) {
    try {
      const response = await transport.delete(
        `${BASE_PATH}/api/meeting_agent/${agentId}`,
      );
      return response.data;
    } catch (error) {
      console.error(
        "An error occurred while executing deleteMeetingAgent: ",
        error,
      );
      throw error;
    }
  },

  async fetchCalendarSettings() {
    try {
      const response = await transport.get(
        `${BASE_PATH}/api/google/calendar/integration`,
      );
      return response.data;
    } catch (error) {
      console.error(
        "An error occurred while executing fetch calendar settings: ",
        error,
      );
      throw error;
    }
  },

  async updateCalendarSettings(payload) {
    try {
      const response = await transport.post(
        `${BASE_PATH}/api/google/calendar/update_current`,
        payload,
      );
      return response.data;
    } catch (error) {
      console.error(
        "An error occurred while executing update calendar settings: ",
        error,
      );
      throw error;
    }
  },

  async disconnectCalendar() {
    try {
      const response = await transport.post(
        `${BASE_PATH}/api/google/calendar/disconnect`,
      );
      return response.data;
    } catch (error) {
      console.error("An error occurred while disconnecting calendar: ", error);
      throw error;
    }
  },

  /* --------------- Open Tracking APIs --------------- */

  async setOpenTracking(enabled) {
    try {
      let res = await transport.post(
        `${BASE_PATH}/api/mailboxes/set_open_tracking`,
        { open_tracking_enabled: enabled },
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing setOpenTracking: ",
        error,
      );
      throw error;
    }
  },

  async fetchOpenTrackingState() {
    try {
      let res = await transport.get(
        `${BASE_PATH}/api/mailboxes/open_tracking_state`,
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing fetchOpenTrackingState: ",
        error,
      );
      throw error;
    }
  },

  async setClickTracking(enabled) {
    try {
      let res = await transport.post(
        `${BASE_PATH}/api/mailboxes/set_click_tracking`,
        { click_tracking_enabled: enabled },
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing setClickTracking: ",
        error,
      );
      throw error;
    }
  },

  async fetchCnameDomainTracking() {
    try {
      let res = await transport.get(
        `${BASE_PATH}/api/mailboxes/fetch_domain_tracking`,
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing fetchCnameDomainTracking: ",
        error,
      );
      throw error;
    }
  },

  async setCnameDomainTracking(domain) {
    try {
      let res = await transport.post(
        `${BASE_PATH}/api/mailboxes/set_domain_tracking`,
        { domain: domain },
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing setCnameDomainTracking: ",
        error,
      );
      throw error;
    }
  },

  async validateDomainTracking(domain) {
    try {
      let res = await transport.post(
        `${BASE_PATH}/api/mailboxes/validate_domain_tracking`,
        { domain: domain },
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing validateDomainTracking: ",
        error,
      );
      throw error;
    }
  },

  async fetchClickTrackingState() {
    try {
      let res = await transport.get(
        `${BASE_PATH}/api/mailboxes/click_tracking_state`,
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing fetchClickTrackingState: ",
        error,
      );
      throw error;
    }
  },

  async setMeetingLeadAutocreate(enabled) {
    try {
      let res = await transport.post(
        `${BASE_PATH}/api/mailboxes/set_meeting_lead_autocreate`,
        { meeting_lead_autocreate_enabled: enabled },
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing setMeetingLeadAutocreate: ",
        error,
      );
      throw error;
    }
  },

  async fetchMeetingLeadAutocreateState() {
    try {
      let res = await transport.get(
        `${BASE_PATH}/api/mailboxes/meeting_lead_autocreate_state`,
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing fetchMeetingLeadAutocreateState: ",
        error,
      );
      throw error;
    }
  },

  async fetchPhoneNumberContact(params) {
    try {
      let res = await transport.get(`${BASE_PATH}/api/phone_number/contact`, {
        params: params,
      });
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing fetchPhoneNumberContact: ",
        error,
      );
      throw error;
    }
  },

  /* ---------- Availability APIs --------------- */

  async getUserAvailability() {
    try {
      const response = await transport.get(
        `${BASE_PATH}/api/user_availability`,
      );
      return response.data;
    } catch (error) {
      console.error(
        "An error occurred while executing getUserAvailability: ",
        error,
      );
      throw error;
    }
  },

  async createUserAvailability(data) {
    try {
      const response = await transport.post(
        `${BASE_PATH}/api/user_availability`,
        data,
      );
      return response.data;
    } catch (error) {
      console.error(
        "An error occurred while executing createUserAvailability: ",
        error,
      );
      throw error;
    }
  },

  async updateUserAvailability(data) {
    try {
      const response = await transport.put(
        `${BASE_PATH}/api/user_availability`,
        data,
      );
      return response.data;
    } catch (error) {
      console.error(
        "An error occurred while executing updateUserAvailability: ",
        error,
      );
      throw error;
    }
  },

  async updateDefaultMeetingLink(payload) {
    try {
      const response = await transport.put(
        `${BASE_PATH}/api/user/meeting_link`,
        payload,
      );
      return response.data;
    } catch (error) {
      console.error(
        "An error occurred while executing updateDefaultMeetingLink: ",
        error,
      );
      throw error;
    }
  },

  async deleteUserAvailability() {
    try {
      const response = await transport.delete(
        `${BASE_PATH}/api/user_availability`,
      );
      return response.data;
    } catch (error) {
      console.error(
        "An error occurred while executing deleteUserAvailability: ",
        error,
      );
      throw error;
    }
  },

  /* --------------- Save Search APIs --------------- */

  async getSavedSearches(params) {
    try {
      let res = await transport.get(`${BASE_PATH}/api/prospect_filter/all`, {
        params: params,
      });
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing getSavedSearches: ",
        error,
      );
      throw error;
    }
  },

  async saveSearch(payload) {
    try {
      let res = await transport.post(
        `${BASE_PATH}/api/prospect_filter/create`,
        payload,
      );
      return res.data;
    } catch (error) {
      console.error("An error occurred while executing saveSearch: ", error);
      throw error;
    }
  },

  async updateSavedSearch(id, payload) {
    try {
      let res = await transport.put(
        `${BASE_PATH}/api/prospect_filter/update/${id}`,
        payload,
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing updateSavedSearch: ",
        error,
      );
      throw error;
    }
  },

  async deleteSavedSearch(id) {
    try {
      let res = await transport.delete(
        `${BASE_PATH}/api/prospect_filter/delete/${id}`,
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing deleteSavedSearch: ",
        error,
      );
      throw error;
    }
  },

  async markAsPrimary(mailboxId) {
    try {
      let res = await transport.post(
        `${BASE_PATH}/api/mailboxes/mark_as_primary`,
        {
          mailbox_id: mailboxId,
        },
      );
      return res.data;
    } catch (error) {
      console.error("An error occurred while executing markAsPrimary: ", error);
      throw error;
    }
  },

  async addMailboxes(mailboxes, is_team) {
    try {
      let res = await transport.post(`${BASE_PATH}/api/mailboxes`, {
        mailboxes,
        is_team,
      });
      return res.data;
    } catch (error) {
      console.error("An error occurred while executing addMailboxes: ", error);
      throw error;
    }
  },

  async reconnectMailbox(emailId) {
    try {
      let res = await transport.post(`${BASE_PATH}/api/mailboxes/reconnect`, {
        id: emailId,
      });
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing reconnectMailbox: ",
        error,
      );
      throw error;
    }
  },

  /* --------------- Email/Domain Blocklists APIs --------------- */
  async getEmailAndDomainBlocklists() {
    try {
      let res = await transport.get(`${BASE_PATH}/api/blocklists`);
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing getEmailAndDomainBlocklists: ",
        error,
      );
      throw error;
    }
  },

  async addEmailAndDomainBlocklist(payload) {
    try {
      let res = await transport.post(`${BASE_PATH}/api/blocklists`, payload);
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing addEmailAndDomainBlocklist: ",
        error,
      );
      throw error;
    }
  },

  async deleteEmailAndDomainBlocklist(id, type) {
    try {
      let res = await transport.delete(
        `${BASE_PATH}/api/blocklists/${type}/${id}`,
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing deleteEmailAndDomainBlocklist: ",
        error,
      );
      throw error;
    }
  },

  /* --------------- Primary and Secondary Linkedin APIs --------------- */

  async getPrimaryLinkedInAccount(data) {
    try {
      let res = await transport.post(
        `${BASE_PATH}/api/linkedin/primary_linkedin_account`,
        data,
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing getPrimaryLinkedInAccount: ",
        error,
      );
      throw error;
    }
  },

  async connectPrimaryLinkedInAccount(data) {
    try {
      let res = await transport.post(
        `${BASE_PATH}/api/linkedin/connect_primary_linkedin_account`,
        data,
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing connectPrimaryLinkedInAccount: ",
        error,
      );
      throw error;
    }
  },

  async saveLinkedinSettings(params) {
    try {
      let res = await transport.post(`${BASE_PATH}/api/linkedin/settings`, {
        params: params,
      });
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing saveLinkedinSettings: ",
        error,
      );
      throw error;
    }
  },

  async fetchLinkedinSettings(data) {
    try {
      let res = await transport.post(
        `${BASE_PATH}/api/linkedin/fetch_settings`,
        data,
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing fetchLinkedinSettings: ",
        error,
      );
      throw error;
    }
  },

  async fetchAllLinkedInIntegrations() {
    try {
      let res = await transport.get(
        `${BASE_PATH}/api/linkedin/fetch_all_integrations`,
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing fetchAllLinkedInIntegrations: ",
        error,
      );
      throw error;
    }
  },

  /* --------------- Exports APIs --------------- */

  async fetchExports(params) {
    try {
      let res = await transport.get(`${BASE_PATH}/api/exports`, {
        params: params,
      });
      return res.data;
    } catch (error) {
      console.error("An error occurred while executing fetchExports: ", error);
      throw error;
    }
  },

  async exportList(list_id, payload) {
    try {
      let res = await transport.post(
        `${BASE_PATH}/api/entity_lists/${list_id}/export`,
        payload,
      );
      return res.data;
    } catch (error) {
      console.error("An error occurred while executing exportList: ", error);
      throw error;
    }
  },

  async fetchEmailSettings(payload) {
    try {
      let res = await transport.post(
        `${BASE_PATH}/api/email/fetch_settings`,
        payload,
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing fetchEmailSettings: ",
        error,
      );
      throw error;
    }
  },

  async saveEmailSettings(payload) {
    try {
      let res = await transport.post(
        `${BASE_PATH}/api/email/save_settings`,
        payload,
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing saveEmailSettings: ",
        error,
      );
      throw error;
    }
  },

  async exportView(view_id, payload) {
    try {
      let res = await transport.post(
        `${BASE_PATH}/api/views/${view_id}/export`,
        payload,
      );
      return res.data;
    } catch (error) {
      console.error("An error occurred while executing exportView: ", error);
      throw error;
    }
  },

  async exportTasks(params) {
    try {
      let res = await transport.post(
        `${BASE_PATH}/api/projects/csvbox/export`,
        params,
      );
      return res.data;
    } catch (error) {
      console.error("An error occurred while executing exportTasks: ", error);
      throw error;
    }
  },

  async exportMeetingLogsView(filterData, pageParams = {}) {
    try {
      const params = {
        agent_id: filterData.meeting_agent || null,
        time_range: filterData.date || { option: "all_time" },
        status: filterData.result_types || null,
        tags: filterData.tags || null,
        host_emails: filterData.host_emails || null,
        dispositions: filterData.disposition || null,
        meeting_link: filterData.meeting_link || null,
        source: pageParams.source || null,
        ai_notetaker_filter: filterData.ai_notetaker || null,
        is_synced_from_calendar: filterData.is_synced_from_calendar || null,
        page: 1,
        limit: -1,
      };

      Object.keys(params).forEach((key) => {
        if (params[key] === null || params[key] === undefined) {
          delete params[key];
        }
      });

      return await transport.get(`${BASE_PATH}/api/meeting_agent/logs/export`, {
        params: params,
        responseType: "blob",
      });
    } catch (error) {
      console.error(
        "An error occurred while executing exportMeetingLogsView: ",
        error,
      );
      throw error;
    }
  },

  /* --------------- Linkedin disconnection --------------- */

  async disconnectLinkedIn(payload) {
    try {
      let res = await transport.post(`${BASE_PATH}/api/linkedin/disconnect`, {
        payload,
      });
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing disconnectLinkedIn: ",
        error,
      );
      throw error;
    }
  },

  async setLinkedInAsPrimary(integrationId) {
    try {
      let res = await transport.post(
        `${BASE_PATH}/api/linkedin/set_as_primary`,
        {
          integration_id: integrationId,
        },
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing setLinkedInAsPrimary: ",
        error,
      );
      throw error;
    }
  },

  async changeContactPrimaryEmail(id, payload) {
    try {
      let res = await transport.post(
        `${BASE_PATH}/api/contacts/${id}/change_primary_email`,
        payload,
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing changeContactPrimaryEmail: ",
        error,
      );
      throw error;
    }
  },

  async changeContactPrimaryPhoneNumber(contact_id, payload) {
    try {
      let res = await transport.post(
        `${BASE_PATH}/api/contacts/${contact_id}/change_primary_phone_number`,
        payload,
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing changeContactPrimaryPhoneNumber: ",
        error,
      );
      throw error;
    }
  },

  async changeLeadPrimaryEmail(lead_id, payload) {
    try {
      let res = await transport.post(
        `${BASE_PATH}/api/leads/${lead_id}/change_primary_email`,
        payload,
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing changeLeadPrimaryEmail: ",
        error,
      );
      throw error;
    }
  },

  async changeLeadPrimaryPhoneNumber(lead_id, payload) {
    try {
      let res = await transport.post(
        `${BASE_PATH}/api/leads/${lead_id}/change_primary_phone_number`,
        payload,
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing changeLeadPrimaryPhoneNumber: ",
        error,
      );
      throw error;
    }
  },

  /* --------------- PLAYBOOK FORMS --------------- */

  async getPlaybookForms(params) {
    try {
      let res = await transport.get(`${BASE_PATH}/api/play_book_form`, {
        params: params,
      });
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing getPlaybookForms: ",
        error,
      );
      throw error;
    }
  },

  /* --------------- BULK ASSIGNMENT --------------- */

  async changeEntityOwner(entity, payload) {
    try {
      let res = await transport.post(
        `${BASE_PATH}/api/${entity}/change_owner`,
        payload,
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing changeEntityOwner: ",
        error,
      );
      throw error;
    }
  },

  async changeTaskDueDate(entityType, payload) {
    try {
      const res = await transport.post(
        `${BASE_PATH}/api/tasks/bulk_update_due_date`,
        payload,
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing changeTaskDueDate: ",
        error,
      );
      throw error;
    }
  },

  /* --------------Objects APIs -----------------*/
  async fetchObjectProperties(entityType) {
    try {
      let res = await transport.get(
        `${BASE_PATH}/api/entity/details/${entityType}`,
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing fetchObjectProperties: ",
        error,
      );
      throw error;
    }
  },

  async createObjectProperty(entityType, payload) {
    try {
      let res = await transport.post(
        `${BASE_PATH}/api/${entityType}/custom_properties`,
        payload,
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing createObjectProperty: ",
        error,
      );
      throw error;
    }
  },

  async fetchObjectPropertyDetails(entityType, id) {
    try {
      let res = await transport.get(
        `${BASE_PATH}/api/${entityType}/custom_properties/${id}`,
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing fetchObjectPropertyDetails: ",
        error,
      );
      throw error;
    }
  },

  async updateObjectPropertyDetails(entityType, id, payload) {
    try {
      let res = await transport.patch(
        `${BASE_PATH}/api/${entityType}/custom_properties/${id}`,
        payload,
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing updateObjectPropertyDetails: ",
        error,
      );
      throw error;
    }
  },

  async updateObjectPropertyOptions(entityType, property, options) {
    try {
      let res = await transport.put(
        `${BASE_PATH}/api/entity/property_options/${entityType}/${property}`,
        { options },
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing updateObjectPropertyOptions: ",
        error,
      );
      throw error;
    }
  },

  async deleteObjectProperty(entityType, id) {
    try {
      let res = await transport.delete(
        `${BASE_PATH}/api/${entityType}/custom_properties/${id}`,
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing deleteObjectProperty: ",
        error,
      );
      throw error;
    }
  },

  async fetchCustomerSettings() {
    try {
      let res = await transport.get(
        `${BASE_PATH}/api/entity/customer_settings`,
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing fetchCustomerSettings: ",
        error,
      );
      throw error;
    }
  },

  async updateCustomerSettings(customerObject, customerLifecycleStages) {
    try {
      let res = await transport.put(
        `${BASE_PATH}/api/entity/customer_settings`,
        {
          customer_object: customerObject,
          customer_lifecycle_stages: customerLifecycleStages,
        },
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing updateCustomerSettings: ",
        error,
      );
      throw error;
    }
  },

  async fetchObjectPropertyStatus(entityType, status) {
    try {
      let res = await transport.get(
        `${BASE_PATH}/api/entity/property_details/${entityType}/${status}`,
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing fetchObjectPropertyStatus: ",
        error,
      );
      throw error;
    }
  },

  async addObjectStatusOption(entityType, option) {
    try {
      let res = await transport.post(
        `${BASE_PATH}/api/entity/status_options/${entityType}`,
        { option },
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing addObjectStatusOption: ",
        error,
      );
      throw error;
    }
  },

  async addObjectPropertyOption(entityType, property, option) {
    try {
      let res = await transport.post(
        `${BASE_PATH}/api/entity/property_options/${entityType}/${property}`,
        { option },
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing addObjectPropertyOption: ",
        error,
      );
      throw error;
    }
  },

  async deleteObjectStatusOption(entityType, option) {
    try {
      let res = await transport.delete(
        `${BASE_PATH}/api/entity/status_options/${entityType}/${option}`,
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing deleteObjectStatusOption: ",
        error,
      );
      throw error;
    }
  },

  async deleteObjectPropertyOption(entityType, property, option) {
    try {
      let res = await transport.delete(
        `${BASE_PATH}/api/entity/property_options/${entityType}/${property}/${option}`,
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing deleteObjectPropertyOption: ",
        error,
      );
      throw error;
    }
  },

  async verifyDeleteStatusOption(entityType, option) {
    try {
      let res = await transport.get(
        `${BASE_PATH}/api/entity/status_options/${entityType}/${option}/usage`,
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing verifyDeleteStatusOption: ",
        error,
      );
      throw error;
    }
  },

  async verifyDeletePropertyOption(entityType, property, option) {
    try {
      let res = await transport.get(
        `${BASE_PATH}/api/entity/property_options/${entityType}/${property}/${option}/usage`,
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing verifyDeletePropertyOption: ",
        error,
      );
      throw error;
    }
  },

  async changeListName(id, name) {
    try {
      let res = await transport.post(
        `${BASE_PATH}/api/entity_lists/${id}/change_name`,
        { name: name },
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing changeListName: ",
        error,
      );
      throw error;
    }
  },

  async checkEmail(email) {
    try {
      let res = await transport.post(BASE_PATH + "/check_email", {
        email: email,
      });
      return res.data;
    } catch (error) {
      console.error("An error occurred while executing checkEmail: ", error);
      throw error;
    }
  },

  //   ----------------- Routing Forms ---------------------

  async getForms(params = {}) {
    try {
      let res = await transport.get(`${BASE_PATH}/api/forms`, {
        params: params,
      });
      return res.data;
    } catch (error) {
      console.error("An error occurred while executing getForms: ", error);
      throw error;
    }
  },

  //---------------data enrichment apis----------------

  async getColumnMapping(payload) {
    try {
      let res = await transport.post(
        `${BASE_PATH}/api/v1/column_mapping`,
        payload,
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing getColumnMapping: ",
        error,
      );
      throw error;
    }
  },

  async enrichPeopleCSV(payload) {
    try {
      let res = await transport.post(
        `${BASE_PATH}/api/enrich_people_csv`,
        payload,
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing enrichPeopleCSV: ",
        error,
      );
      throw error;
    }
  },

  async enrichCompanyCSV(payload) {
    try {
      let res = await transport.post(
        `${BASE_PATH}/api/enrich_companies_csv`,
        payload,
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing enrichCompanyCSV: ",
        error,
      );
      throw error;
    }
  },

  async getActiveCSVEnrichments() {
    try {
      let res = await transport.get(
        `${BASE_PATH}/api/csv_enrichment/active_jobs`,
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing getActiveCSVEnrichments: ",
        error,
      );
      throw error;
    }
  },

  async enrichPeopleList(payload) {
    try {
      let res = await transport.post(
        `${BASE_PATH}/api/enrich_contact_list`,
        payload,
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing enrichPeopleList: ",
        error,
      );
      throw error;
    }
  },

  async enrichCompanyList(payload) {
    try {
      let res = await transport.post(
        `${BASE_PATH}/api/enrich_company_list`,
        payload,
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing enrichCompanyList: ",
        error,
      );
      throw error;
    }
  },

  async fetchEnrichment(
    page,
    page_size,
    searchQuery,
    typeFilter,
    createdByFilter,
    uploadedOnFilter,
  ) {
    try {
      let res = await transport.get(BASE_PATH + "/api/enrichments", {
        params: {
          page: page,
          page_size: page_size,
          query: searchQuery,
          type: typeFilter,
          created_by: createdByFilter,
          date: uploadedOnFilter,
        },
      });
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing fetchEnrichment: ",
        error,
      );
      throw error;
    }
  },

  async createForm(formData) {
    try {
      const res = await transport.post(`${BASE_PATH}/api/forms`, formData);
      return res.data;
    } catch (error) {
      console.error("An error occurred while executing createForm: ", error);
      throw error;
    }
  },

  async fetchFormsById(formId) {
    try {
      const res = await transport.get(`${BASE_PATH}/api/forms/${formId}`);
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing fetchFormsById: ",
        error,
      );
      throw error;
    }
  },

  async updateForm(formId, formData) {
    try {
      const res = await transport.put(
        `${BASE_PATH}/api/forms/${formId}`,
        formData,
      );
      return res.data;
    } catch (error) {
      console.error("An error occurred while executing updateForm: ", error);
      throw error;
    }
  },

  async deleteFormById(formId) {
    try {
      const res = await transport.delete(`${BASE_PATH}/api/forms/${formId}`);
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing deleteFormById: ",
        error,
      );
      throw error;
    }
  },

  async fetchQuestionById(formId) {
    try {
      const res = await transport.get(
        `${BASE_PATH}/api/forms/questions/${formId}`,
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing fetchQuestionById: ",
        error,
      );
      throw error;
    }
  },

  async saveQuestionById(formId, questionData) {
    try {
      const res = await transport.post(
        `${BASE_PATH}/api/forms/questions/${formId}`,
        questionData,
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing saveQuestionById: ",
        error,
      );
      throw error;
    }
  },

  async fetchRoutesById(formId) {
    try {
      const res = await transport.get(
        `${BASE_PATH}/api/forms/routes/${formId}`,
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing fetchRoutesById: ",
        error,
      );
      throw error;
    }
  },

  async fetchLeadRoutesById(formId) {
    try {
      const res = await transport.get(
        `${BASE_PATH}/api/forms/lead_routes/${formId}`,
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing fetchLeadRoutesById: ",
        error,
      );
      throw error;
    }
  },

  async saveRoutesById(formId, routeData) {
    try {
      const res = await transport.post(
        `${BASE_PATH}/api/forms/routes/${formId}`,
        routeData,
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing saveRoutesById: ",
        error,
      );
      throw error;
    }
  },

  async saveLeadRoutesById(formId, routeData) {
    try {
      const res = await transport.post(
        `${BASE_PATH}/api/forms/lead_routes/${formId}`,
        routeData,
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing saveLeadRoutesById: ",
        error,
      );
      throw error;
    }
  },

  async fetchResponseLogsById(formId, params) {
    try {
      let res = await transport.get(
        `${BASE_PATH}/api/forms/answers/${formId}`,
        {
          params: params,
        },
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing fetchResponseLogsById: ",
        error,
      );
      throw error;
    }
  },

  async exportResponseLogs(formId, params) {
    try {
      let res = await transport.get(
        `${BASE_PATH}/api/forms/answers/export/${formId}`,
        {
          params: params,
        },
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing exportResponseLogs: ",
        error,
      );
      throw error;
    }
  },

  // -------------------------NLP Filters---------------------

  async getPersonNLPFilters(payload) {
    try {
      let res = await transport.post(
        `${BASE_PATH}/api/nlp/get_person_filters`,
        payload,
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing getPersonNLPFilters: ",
        error,
      );
      throw error;
    }
  },

  async getCompanyNLPFilters(payload) {
    try {
      let res = await transport.post(
        `${BASE_PATH}/api/nlp/get_company_filters/`,
        payload,
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing getCompanyNLPFilters: ",
        error,
      );
      throw error;
    }
  },

  async getLeadNLPCache(payload) {
    try {
      let res = await transport.post(
        `${BASE_PATH}/api/nlp/prospect/lead_cache`,
        payload,
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing getLeadNLPCache: ",
        error,
      );
      throw error;
    }
  },

  async getCompanyNLPCache(payload) {
    try {
      let res = await transport.post(
        `${BASE_PATH}/api/nlp/prospect/company_cache`,
        payload,
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing getCompanyNLPCache: ",
        error,
      );
      throw error;
    }
  },

  async deleteCompanyAssociation(payload) {
    try {
      const res = await transport.delete(
        `${BASE_PATH}/api/delete_existing_company`,
        {
          data: payload,
        },
      );

      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing deleteCompanyAssociation: ",
        error,
      );
      throw error;
    }
  },

  async deleteLeadAssociation(payload) {
    try {
      const res = await transport.delete(
        `${BASE_PATH}/api/delete_existing_lead`,
        {
          data: payload,
        },
      );

      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing deleteLeadAssociation: ",
        error,
      );
      throw error;
    }
  },

  async deleteContactAssociation(payload) {
    try {
      const res = await transport.delete(
        `${BASE_PATH}/api/delete_existing_contact`,
        {
          data: payload,
        },
      );

      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing deleteContactAssociation: ",
        error,
      );
      throw error;
    }
  },

  async deleteDealAssociation(payload) {
    try {
      const res = await transport.delete(
        `${BASE_PATH}/api/delete_existing_deal`,
        {
          data: payload,
        },
      );

      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing deleteDealAssociation: ",
        error,
      );
      throw error;
    }
  },

  async deleteListAssociation(payload) {
    try {
      const res = await transport.delete(
        `${BASE_PATH}/api/entity_lists/delete_existing_entity`,
        {
          data: payload,
        },
      );

      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing deleteListAssociation: ",
        error,
      );
      throw error;
    }
  },

  // ------------------- Meeting Logs APIs -------------------

  async getMeetingAgentLogs(params) {
    try {
      let res = await transport.get(
        `${BASE_PATH}/api/meeting_agent/logs/index`,
        {
          params: params,
        },
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing getMeetingAgentLogs: ",
        error,
      );
      throw error;
    }
  },

  async getMeetingAgentLogsById(meetingId, params) {
    try {
      let res = await transport.get(
        `${BASE_PATH}/api/meeting_agent/logs/${meetingId}`,
        {
          params: params,
        },
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing getMeetingAgentLogsById: ",
        error,
      );
      throw error;
    }
  },

  async fetchHostEmails() {
    try {
      let res = await transport.get(
        `${BASE_PATH}/api/meeting_agent/logs/hosts`,
      );
      return res.data;
    } catch (error) {
      console.error("An error occurred while fetching host emails: ", error);
      throw error;
    }
  },

  async updateMeeting(meetingId, meetingData) {
    try {
      let res = await transport.put(`${BASE_PATH}/api/meetings/${meetingId}`, {
        meeting: meetingData,
      });
      return res.data;
    } catch (error) {
      console.error("An error occurred while executing updateMeeting: ", error);
      throw error;
    }
  },

  async updateMeetingDisposition(meetingId, disposition) {
    try {
      let res = await transport.patch(
        `${BASE_PATH}/api/meeting/${meetingId}/disposition`,
        { disposition },
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while updating meeting disposition: ",
        error,
      );
      throw error;
    }
  },

  async createMeetingBot(payload) {
    try {
      let res = await transport.post(
        `${BASE_PATH}/api/meeting_agent/create_bot`,
        payload,
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing createMeetingBot: ",
        error,
      );
      throw error;
    }
  },

  async getMeetingLogRecordingDetails(id) {
    try {
      let res = await transport.get(
        `${BASE_PATH}/api/meeting_agent/logs/${id}/recording`,
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing getMeetingLogRecordingDetails: ",
        error,
      );
      throw error;
    }
  },

  async getMeetingLogSummary(id) {
    try {
      let res = await transport.get(
        `${BASE_PATH}/api/meeting_agent/logs/${id}/summary`,
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing getMeetingLogSummary: ",
        error,
      );
      throw error;
    }
  },

  async generateAIEmail(id) {
    try {
      let res = await transport.get(
        `${BASE_PATH}/api/meeting_agent/logs/${id}/email`,
      );
      return res.data;
    } catch (error) {
      console.error("An error occurred while generating AI Email: ", error);
    }
  },

  async fetchMeetingAgentLogsActivity(
    meetingId,
    activity_type,
    page,
    activity_types_filter,
    created_by_id,
    created_date,
  ) {
    try {
      let res = await transport.get(
        BASE_PATH + `/api/meeting_agent/logs/${meetingId}/activity`,
        {
          params: {
            meeting_id: meetingId,
            activity_type: activity_type || "",
            page: page,
            activity_types_filter,
            created_by_id,
            created_date,
          },
        },
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing fetchMeetingAgentLogsActivity: ",
        error,
      );
      throw error;
    }
  },

  async fetchDefineLogicPrompt(params) {
    try {
      let res = await transport.get(`${BASE_PATH}/api/form/routing_prompts`, {
        params: params,
      });
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing fetchDefineLogicPrompt: ",
        error,
      );
      throw error;
    }
  },

  async getDefineLogic(payload) {
    try {
      let res = await transport.post(
        `${BASE_PATH}/api/form/routing_prompts`,
        payload,
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing getDefineLogic: ",
        error,
      );
      throw error;
    }
  },

  async updateDefineLogic(payload) {
    try {
      let res = await transport.put(
        `${BASE_PATH}/api/form/routing_prompts`,
        payload,
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing updateDefineLogic: ",
        error,
      );
      throw error;
    }
  },

  // ------------------- Meeting Recorder Settings APIs -------------------

  async getMeetingRecorderSettings() {
    try {
      const res = await transport.get(
        `${BASE_PATH}/api/meeting_recorder/settings`,
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing getMeetingRecorderSettings: ",
        error,
      );
      throw error;
    }
  },

  async toggleAutoRecord(payload) {
    try {
      const res = await transport.put(
        `${BASE_PATH}/api/meeting_recorder/settings/toggle`,
        payload,
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing toggleAutoRecord: ",
        error,
      );
      throw error;
    }
  },

  async updateRecorderSetting(payload) {
    try {
      const res = await transport.put(
        `${BASE_PATH}/api/meeting_recorder/settings/update`,
        payload,
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing updateRecorderSetting: ",
        error,
      );
      throw error;
    }
  },

  // ------------------------- API Key APIs ---------------------

  async fetchAPIKeys(params) {
    try {
      let res = await transport.get(`${BASE_PATH}/api/user/api_keys`, {
        params: params,
      });
      return res.data;
    } catch (error) {
      console.error("An error occurred while executing fetchAPIKeys: ", error);
      throw error;
    }
  },

  async generateAPIKey(payload) {
    try {
      let res = await transport.post(`${BASE_PATH}/api/user/api_keys`, payload);
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing generateAPIKey: ",
        error,
      );
      throw error;
    }
  },

  async toggleAPIAccess(id, payload) {
    try {
      let res = await transport.put(
        `${BASE_PATH}/api/user/api_keys/access/${id}`,
        payload,
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing toggleAPIAccess: ",
        error,
      );
      throw error;
    }
  },

  async deleteAPIKey(id) {
    try {
      let res = await transport.delete(`${BASE_PATH}/api/user/api_keys/${id}`);
      return res.data;
    } catch (error) {
      console.error("An error occurred while executing deleteAPIKey: ", error);
      throw error;
    }
  },
  // ------------------------- SDR Call Prep APIs ---------------------

  async callPrepByTask(taskId) {
    try {
      let res = await transport.get(
        `${BASE_PATH}/api/call_prep_by_task/${taskId}`,
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing callPrepByTask: ",
        error,
      );
      throw error;
    }
  },

  // ------------------------- Custom property APIs ---------------------

  async fetchCustomProperties(entity_type) {
    try {
      let res = await transport.get(
        `${BASE_PATH}/api/${entity_type}/custom_properties`,
        {},
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing fetchCustomProperties: ",
        error,
      );
      throw error;
    }
  },

  async getMailboxesByVendor(params) {
    try {
      let res = await transport.get(`${BASE_PATH}/api/vendor/mailboxes`, {
        params: params,
      });
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while getting mailboxes by vendor: ",
        error,
      );
    }
  },

  async exportMailboxes(payload) {
    try {
      let res = await transport.post(
        `${BASE_PATH}/api/mailboxes/export`,
        payload,
      );
      return res.data;
    } catch (error) {
      console.error("An error occurred while exporting mailboxes: ", error);
    }
  },

  async triggerSalesNotification() {
    try {
      let res = await transport.post(`${BASE_PATH}/api/trigger/notification`);
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while triggering sales notification: ",
        error,
      );
    }
  },

  async closeVideoSection() {
    try {
      let res = await transport.post(
        `${BASE_PATH}/api/get_started/close/video_section`,
      );
      return res.data;
    } catch (error) {
      console.error("An error occurred while closing video section: ", error);
    }
  },

  async fetchCallCampaignViews() {
    try {
      let res = await transport.get(`${BASE_PATH}/api/call_campaign/views`);
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing fetchCampaignViews: ",
        error,
      );
      throw error;
    }
  },

  async updateCallCampaignView(view_id, data) {
    try {
      let res = await transport.put(`${BASE_PATH}/api/views/${view_id}`, data);
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing updateCampaignView: ",
        error,
      );
      throw error;
    }
  },

  async fetchCallCampaignEntityViews() {
    try {
      let res = await transport.get(
        `${BASE_PATH}/api/call_campaign_entity/views`,
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing fetchCallCampaignEntityViews: ",
        error,
      );
      throw error;
    }
  },

  async getSSOConfiguration() {
    try {
      let res = await transport.get(`${BASE_PATH}/api/saml_configurations`);
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while getting sso configuration: ",
        error,
      );
      throw error;
    }
  },

  async saveSSOConfiguration(payload) {
    try {
      let res = await transport.post(
        `${BASE_PATH}/api/saml_configurations`,
        payload,
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while saving sso configuration: ",
        error,
      );
      throw error;
    }
  },

  async updateSSOConfiguration(id, payload) {
    try {
      let res = await transport.put(
        `${BASE_PATH}/api/saml_configurations/${id}`,
        payload,
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while updating sso configuration: ",
        error,
      );
      throw error;
    }
  },

  async testSSOConfiguration(payload) {
    try {
      let res = await transport.post(
        `${BASE_PATH}/api/saml_configurations/test`,
        payload,
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while testing sso configuration: ",
        error,
      );
      throw error;
    }
  },

  async createSSOConfigurationFromMetadata(metadataUrl, domain) {
    try {
      let res = await transport.post(
        `${BASE_PATH}/api/saml_configurations/from_metadata`,
        {
          metadata_url: metadataUrl,
          domain: domain,
        },
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while creating sso configuration from metadata: ",
        error,
      );
      throw error;
    }
  },

  // ------------------------- Audit Trail APIs ---------------------

  async fetchAuditLogs(params) {
    try {
      let res = await transport.get(
        `${BASE_PATH}/api/audit-logs/entity/${params.entity_type}/${params.entity_id}`,
        {
          params: params,
        },
      );
      return res.data;
    } catch (error) {
      console.error("An error occurred while getting audit logs: ", error);
      throw error;
    }
  },

  async fetchWorkspaceAuditLogs(params) {
    try {
      let res = await transport.get(`${BASE_PATH}/api/audit-logs`, {
        params: params,
      });
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while getting workspace audit logs: ",
        error,
      );
      throw error;
    }
  },

  async fetchAuditByID(id, params) {
    try {
      let res = await transport.get(
        `${BASE_PATH}/api/audit-logs/${id}/entity`,
        {
          params: params,
        },
      );
      return res.data;
    } catch (error) {
      console.error("An error occurred while getting audit by ID: ", error);
      throw error;
    }
  },

  async fetchUserPersonalMailboxes(userIds) {
    try {
      let res = await transport.get(
        BASE_PATH + "/api/user/personal/mailboxes",
        {
          params: { user_ids: userIds },
        },
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing fetchUserPersonalMailboxes: ",
        error,
      );
      throw error;
    }
  },

  async getReferralCode() {
    try {
      let res = await transport.get(`${BASE_PATH}/api/referral_code`);
      return res.data;
    } catch (error) {
      console.error("An error occurred while fetching referral code: ", error);
      throw error;
    }
  },
  // ------------------------- Waterfall APIs ---------------------

  async fetchWaterfallVendors() {
    try {
      let res = await transport.get(
        `${BASE_PATH}/api/v1/waterfall/list_vendors`,
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while fetching waterfall vendors: ",
        error,
      );
      throw error;
    }
  },

  async fetchWaterfallConfig() {
    try {
      let res = await transport.get(
        `${BASE_PATH}/api/v1/waterfall/list_config`,
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while fetching waterfall config: ",
        error,
      );
      throw error;
    }
  },

  async updateWaterfallConfig(configData) {
    try {
      let res = await transport.put(
        `${BASE_PATH}/api/v1/waterfall/update_config`,
        configData,
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while updating waterfall config: ",
        error,
      );
      throw error;
    }
  },

  async fetchWaterfallAccounts() {
    try {
      let res = await transport.get(
        `${BASE_PATH}/api/v1/waterfall/list_accounts`,
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while fetching waterfall accounts: ",
        error,
      );
      throw error;
    }
  },

  async createWaterfallAccount(accountData) {
    try {
      let res = await transport.post(
        `${BASE_PATH}/api/v1/waterfall/create_account`,
        accountData,
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while creating waterfall account: ",
        error,
      );
      throw error;
    }
  },

  async deleteWaterfallAccount(accountId) {
    try {
      let res = await transport.delete(
        `${BASE_PATH}/api/v1/waterfall/delete_account/${accountId}`,
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while deleting waterfall account: ",
        error,
      );
      throw error;
    }
  },

  async enrichWithWaterfall(enrichData) {
    try {
      let res = await transport.post(
        `${BASE_PATH}/api/v1/waterfall/enrich`,
        enrichData,
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while enriching with waterfall: ",
        error,
      );
      throw error;
    }
  },

  async getWaterfallEnrichmentStatus(requestId) {
    try {
      let res = await transport.get(
        `${BASE_PATH}/api/v1/waterfall/status/${requestId}`,
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while fetching waterfall enrichment status: ",
        error,
      );
      throw error;
    }
  },

  async getAllCurrencies() {
    try {
      let res = await transport.get(`${BASE_PATH}/api/currencies`);
      return res.data;
    } catch (error) {
      console.error("An error occurred while fetching all currencies: ", error);
      throw error;
    }
  },

  async getWorkspaceCurrencies() {
    try {
      let res = await transport.get(`${BASE_PATH}/api/workspace/currencies`);
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while fetching workspace currencies: ",
        error,
      );
      throw error;
    }
  },

  async CreateWorkspaceCurrency(params) {
    try {
      let res = await transport.post(
        `${BASE_PATH}/api/workspace/currencies`,
        params,
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while creating workspace currency: ",
        error,
      );
      throw error;
    }
  },

  async markCurrencyAsPrimary(params, id) {
    try {
      let res = await transport.patch(
        `${BASE_PATH}/api/workspace/currencies/${id}`,
        params,
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while marking currency as primary: ",
        error,
      );
      throw error;
    }
  },

  async deleteWorkspaceCurrency(id) {
    try {
      let res = await transport.delete(
        `${BASE_PATH}/api/workspace/currencies/${id}`,
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while deleting workspace currency: ",
        error,
      );
      throw error;
    }
  },

  async fetchLinkedInLogs(page, page_size, filters = {}) {
    try {
      const params = {
        page,
        page_size,
        ...filters,
      };

      let res = await transport.get(`${BASE_PATH}/api/linkedin_logs`, {
        params,
      });
      return res.data;
    } catch (error) {
      console.error("Error fetching LinkedIn logs:", error);
      throw error;
    }
  },

  async exportLinkedInLogs(exportData, filterData) {
    try {
      const payload = {
        ...exportData,
        ...filterData,
      };

      let res = await transport.post(
        `${BASE_PATH}/api/linkedin_logs/export`,
        payload,
      );
      return res.data;
    } catch (error) {
      console.error("Error exporting LinkedIn logs:", error);
      throw error;
    }
  },

  async fetchLinkedInIntegrations() {
    try {
      const res = await transport.get(`${BASE_PATH}/api/linkedin/integrations`);
      return res.data;
    } catch (error) {
      console.error("Error fetching LinkedIn integrations:", error);
      throw error;
    }
  },

  // ------------------------------ Dashboard Builder API ------------------------------
  builder: {
    // Dashboard operations
    async fetchDashboards(params = {}) {
      try {
        let res = await transport.get(`/api/dashboards`, { params });
        return res.data;
      } catch (error) {
        console.error("An error occurred while fetching dashboards: ", error);
        throw error;
      }
    },

    async fetchDashboardByID(id) {
      try {
        let res = await transport.get(`/api/dashboards/${id}`);
        return res.data;
      } catch (error) {
        console.error("An error occurred while fetching dashboard: ", error);
        throw error;
      }
    },

    async createDashboard(payload) {
      try {
        let res = await transport.post(`/api/dashboards`, payload);
        return res.data;
      } catch (error) {
        console.error("An error occurred while creating dashboard: ", error);
        throw error;
      }
    },

    async updateDashboard(id, payload) {
      try {
        let res = await transport.put(`/api/dashboards/${id}`, payload);
        return res.data;
      } catch (error) {
        console.error("An error occurred while updating dashboard: ", error);
        throw error;
      }
    },

    async deleteDashboard(id) {
      try {
        let res = await transport.delete(`/api/dashboards/${id}`);
        return res.data;
      } catch (error) {
        console.error("An error occurred while deleting dashboard: ", error);
        throw error;
      }
    },

    async exportDashboard(uuid, payload) {
      try {
        let res = await transport.post(`/api/dashboards/${uuid}/export`, payload);
        return res.data;
      } catch (error) {
        console.error("An error occurred while exporting dashboard: ", error);
        throw error;
      }
    },

    async addComponentToSection(id, payload) {
      try {
        let res = await transport.post(
          `/api/dashboards/${id}/add-component`,
          payload,
        );
        return res.data;
      } catch (error) {
        console.error(
          "An error occurred while adding component to dashboard: ",
          error,
        );
        throw error;
      }
    },

    async fetchTemplateCategories(params) {
      try {
        const res = await transport.get(
          `/api/dashboards/templates/categories`,
          {
            params: params,
          },
        );
        return res.data;
      } catch (error) {
        console.error(
          "An error occurred while fetching template categories: ",
          error,
        );
        throw error;
      }
    },

    async applyTemplate(id, payload) {
      try {
        const res = await transport.post(
          `/api/dashboards/templates/${id}/apply`,
          payload,
        );
        return res.data;
      } catch (error) {
        console.error(
          "An error occurred while applying dashboard template: ",
          error,
        );
        throw error;
      }
    },

    async fetchListOfCategoryApps() {
      try {
        const res = await transport.get(
          `/api/dashboards/templates/installed-apps`,
        );
        return res.data;
      } catch (error) {
        console.error(
          "An error occurred while fetching list of category apps: ",
          error,
        );
        throw error;
      }
    },

    // Component operations
    async fetchComponents(params = {}) {
      try {
        let res = await transport.get(`/api/dashboard/components`, { params });
        return res.data;
      } catch (error) {
        console.error("An error occurred while fetching components: ", error);
        throw error;
      }
    },

    async fetchComponent(id) {
      try {
        let res = await transport.get(`/api/dashboard/components/${id}`);
        return res.data;
      } catch (error) {
        console.error("An error occurred while fetching component: ", error);
        throw error;
      }
    },

    async createComponent(payload) {
      try {
        let res = await transport.post(
          `/api/admin/dashboard/components`,
          payload,
        );
        return res.data;
      } catch (error) {
        console.error("An error occurred while creating component: ", error);
        throw error;
      }
    },

    async updateComponent(id, payload) {
      try {
        let res = await transport.put(
          `/api/admin/dashboard/components/${id}`,
          payload,
        );
        return res.data;
      } catch (error) {
        console.error("An error occurred while updating component: ", error);
        throw error;
      }
    },

    async deleteComponent(id) {
      try {
        let res = await transport.delete(
          `/api/admin/dashboard/components/${id}`,
        );
        return res.data;
      } catch (error) {
        console.error("An error occurred while deleting component: ", error);
        throw error;
      }
    },
  },

  async updateAIHealthScoringCriteria(payload) {
    try {
      const res = await transport.post(
        `${BASE_PATH}/api/v1/ai-health-scoring-criteria`,
        payload,
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing updateAIHealthScoringCriteria: ",
        error,
      );
      throw error;
    }
  },

  async fetchAIHealthScoringCriteria() {
    try {
      const res = await transport.get(
        `${BASE_PATH}/api/v1/ai-health-scoring-criteria`,
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing fetchAIHealthScoringCriteria: ",
        error,
      );
      throw error;
    }
  },

  async updateMultipleStandardObjects(slug, payload) {
    try {
      let res = await transport.patch(`${BASE_PATH}/api/${slug}`, payload);
      return res.data;
    } catch (error) {
      console.error(
        `An error occurred while executing updateMultipleStandardObjects for ${slug}: `,
        error,
      );
      throw error;
    }
  },

  async deleteMultipleStandardObjects(slug, payload) {
    try {
      let res = await transport.delete(`${BASE_PATH}/api/${slug}`, {
        data: payload,
      });
      return res.data;
    } catch (error) {
      console.error(
        `An error occurred while executing deleteMultipleStandardObjects for ${slug}: `,
        error,
      );
      throw error;
    }
  },

  async getSlackMessagesByObject(params) {
    try {
      const res = await transport.get(
        `${BASE_PATH}/api/slack/messages/by-object`,
        {
          params: {
            object_type: params.objectType,
            object_id: params.objectId,
            page: params.page || 1,
            limit: params.limit || 25,
          },
        },
      );
      return res.data;
    } catch (error) {
      console.error("An error occurred while fetching Slack messages: ", error);
      throw error;
    }
  },

  async fetchTaskSettings() {
    try {
      const res = await transport.get(`${BASE_PATH}/api/task_settings`);
      return res.data;
    } catch (error) {
      console.error("An error occurred while fetching task settings: ", error);
      throw error;
    }
  },

  async fetchAttachmentById(attachmentId) {
    try {
      const res = await transport.get(`${BASE_PATH}/api/attachments/${attachmentId}`);
      return res.data;
    } catch (error) {
      console.error("An error occurred while fetching attachment: ", error);
      throw error;
    }
  },
};
